import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaSearch, FaSync, FaFilter, FaTimes,
  FaCalendarAlt, FaTags, FaChevronDown, FaFileInvoice,
  FaCheckCircle, FaExclamationTriangle, FaRupeeSign,
  FaChartBar, FaHandHoldingHeart, FaPray, FaMoneyBillWave,
  FaBalanceScale, FaPrint, FaFilePdf, FaArrowUp, FaArrowDown,
  FaGlobe, FaDownload
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ═══════════════════════════════════════════════════
   TOAST COMPONENT
═══════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: { icon: <FaCheckCircle />, label: "Success", accentColor: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", borderColor: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />,       label: "Error",   accentColor: "#8b1a1a", iconBg: "rgba(139,26,26,0.1)",  borderColor: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaSearch />,      label: "Info",    accentColor: "#8a6020", iconBg: "rgba(201,151,58,0.1)", borderColor: "rgba(201,151,58,0.3)", symbol: "ॐ" }
  };
  const c = config[type] || config.info;
  return (
    <div className="rpt-toast-container">
      <div className="rpt-toast-card" style={{ "--accent": c.accentColor, "--border-color": c.borderColor }}>
        <div className="rpt-toast-top-bar" />
        <span className="rpt-corner tl" /><span className="rpt-corner tr" />
        <span className="rpt-corner bl" /><span className="rpt-corner br" />
        <div className="rpt-toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="rpt-toast-spin-ring" />
          <div className="rpt-toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="rpt-toast-content">
          <div className="rpt-toast-label-row">
            <span className="rpt-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="rpt-toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="rpt-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="rpt-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accentColor}55,transparent)` }} />
          <p className="rpt-toast-message">{message}</p>
        </div>
        <button className="rpt-toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="rpt-toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   PDF PREVIEW MODAL  — with i18n + Download
═══════════════════════════════════════════════════ */
function PdfPreviewModal({ reportData, filters, onClose, t, i18n }) {
  const printRef = useRef();
  const [downloading, setDownloading] = useState(false);

  const currentLang = i18n?.language?.startsWith("ml") ? "ml" : "en";
  const togglePdfLang = () => i18n?.changeLanguage(currentLang === "en" ? "ml" : "en");

  const fmtAmt  = (n) => `₹${parseFloat(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  const fmtDate = (v) => { if (!v) return "—"; const d = new Date(v); return isNaN(d) ? v : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); };

  const periodLabel = (() => {
    if (filters.specificDate) return new Date(filters.specificDate).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    if (filters.month) { const [y,m] = filters.month.split("-"); return new Date(y, m-1).toLocaleDateString("en-IN", { month: "long", year: "numeric" }); }
    if (filters.year) return `Year ${filters.year}`;
    if (filters.dateRange?.start || filters.dateRange?.end) {
      const s = filters.dateRange.start ? new Date(filters.dateRange.start).toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"}) : "Beginning";
      const e = filters.dateRange.end   ? new Date(filters.dateRange.end).toLocaleDateString("en-IN",   {day:"2-digit",month:"short",year:"numeric"}) : "Present";
      return `${s} — ${e}`;
    }
    return t ? t("rptFilterAllTime") : "All Records";
  })();

  const { donations, bookings, expenses } = reportData;
  const totalDonations = donations.reduce((s,d) => s + parseFloat(d.amount||0), 0);
  const totalBookings  = bookings.reduce((s,b)  => s + parseFloat(b.amount||0), 0);
  const totalIncome    = totalDonations + totalBookings;
  const totalExpenses  = expenses.reduce((s,e)  => s + parseFloat(e.amount||0), 0);
  const netBalance     = totalIncome - totalExpenses;

  // Group expenses by category
  const expByCat = {};
  expenses.forEach(e => {
    const cat = e.category_title || "Uncategorized";
    if (!expByCat[cat]) expByCat[cat] = { items: [], type: e.category_type || "" };
    expByCat[cat].items.push(e);
  });

  // Donation type breakdown
  const donByType = {};
  donations.forEach(d => {
    const k = d.donation_type || "Other";
    if (!donByType[k]) donByType[k] = { count: 0, total: 0 };
    donByType[k].count++; donByType[k].total += parseFloat(d.amount||0);
  });

  // Offering type breakdown
  const bkByType = {};
  bookings.forEach(b => {
    const k = b.category_name || b.vazhipadu_name || "Other";
    if (!bkByType[k]) bkByType[k] = { count: 0, total: 0 };
    bkByType[k].count++; bkByType[k].total += parseFloat(b.amount||0);
  });

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const css = `
      @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Noto+Sans+Malayalam:wght@400;700&display=swap');
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:'EB Garamond',serif;background:#fff;color:#1a0404;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      .pdf-wrap{max-width:900px;margin:0 auto;padding:0;}

      /* ── HEADER ── */
      .pdf-header{background:linear-gradient(135deg,#2e0c0c 0%,#5a1515 60%,#3d0c0c 100%);padding:36px 52px 28px;position:relative;overflow:hidden;}
      .pdf-header::before{content:'ॐ';position:absolute;right:36px;top:50%;transform:translateY(-50%);font-size:130px;color:rgba(232,196,106,0.06);font-family:'Cinzel',serif;line-height:1;pointer-events:none;}
      .pdf-header-gold-bar{position:absolute;bottom:0;left:0;right:0;height:4px;background:linear-gradient(90deg,transparent 0%,#8a6020 20%,#e8c46a 50%,#8a6020 80%,transparent 100%);}
      .pdf-hdr-row1{display:flex;align-items:center;gap:20px;margin-bottom:18px;}
      .pdf-hdr-seal{width:72px;height:72px;border-radius:50%;background:rgba(232,196,106,0.1);border:2px solid rgba(232,196,106,0.4);display:flex;align-items:center;justify-content:center;font-size:28px;color:#e8c46a;font-family:'Cinzel',serif;flex-shrink:0;box-shadow:0 0 20px rgba(232,196,106,0.1);overflow:hidden;position:relative;}
      .pdf-hdr-logo-img{width:100%;height:100%;object-fit:cover;border-radius:50%;}
      .pdf-hdr-logo-fallback{display:none;font-size:28px;color:#e8c46a;font-family:'Cinzel',serif;align-items:center;justify-content:center;}
      .pdf-hdr-temple-mal{font-family:'Noto Sans Malayalam','Manjari',serif;font-size:18px;font-weight:700;color:#fff8e7;line-height:1.4;margin-bottom:3px;}
      .pdf-hdr-temple-name{font-family:'Cinzel',serif;font-size:14px;font-weight:700;color:#e8c46a;letter-spacing:1.5px;line-height:1.3;}
      .pdf-hdr-temple-sub{font-family:'EB Garamond',serif;font-size:13px;color:rgba(232,196,106,0.5);letter-spacing:1px;margin-top:5px;}
      .pdf-hdr-divider{height:1px;background:linear-gradient(90deg,transparent,rgba(232,196,106,0.35),transparent);margin:0 0 16px;}
      .pdf-hdr-row2{display:flex;align-items:flex-end;justify-content:space-between;}
      .pdf-hdr-report-label{font-family:'Cinzel',serif;font-size:11px;font-weight:600;letter-spacing:3px;text-transform:uppercase;color:rgba(232,196,106,0.6);margin-bottom:5px;}
      .pdf-hdr-report-title{font-family:'Cinzel',serif;font-size:20px;font-weight:700;color:#e8c46a;letter-spacing:1.5px;}
      .pdf-hdr-period{font-family:'EB Garamond',serif;font-size:14px;color:rgba(232,196,106,0.8);margin-top:5px;font-style:italic;}
      .pdf-hdr-meta{text-align:right;}
      .pdf-hdr-meta-line{font-family:'Cinzel',serif;font-size:10px;color:rgba(232,196,106,0.45);letter-spacing:1px;line-height:1.8;}

      /* ── BODY WRAPPER ── */
      .pdf-body{padding:32px 52px 48px;}

      /* ── SUMMARY STRIP ── */
      .pdf-summary-strip{display:grid;grid-template-columns:repeat(5,1fr);border:1.5px solid #e0c070;border-radius:6px;overflow:hidden;margin-bottom:36px;box-shadow:0 2px 8px rgba(201,151,58,0.08);}
      .pdf-sc{padding:16px 12px;text-align:center;border-right:1px solid #e8d89a;position:relative;}
      .pdf-sc:last-child{border-right:none;}
      .pdf-sc-label{font-family:'Cinzel',serif;font-size:7px;letter-spacing:1.5px;text-transform:uppercase;color:#8a6020;margin-bottom:8px;}
      .pdf-sc-val{font-family:'Cinzel',serif;font-size:15px;font-weight:700;letter-spacing:0.5px;}
      .pdf-sc-val.green{color:#1a5c1a;}
      .pdf-sc-val.red{color:#8b1a1a;}
      .pdf-sc-note{font-family:'EB Garamond',serif;font-size:11px;color:#b8a070;margin-top:4px;}
      .pdf-sc.income{background:linear-gradient(180deg,#f0fff0,#fff);}
      .pdf-sc.expense{background:linear-gradient(180deg,#fff5f5,#fff);}
      .pdf-sc.balance-pos{background:linear-gradient(180deg,#e8f5e8,#f0fff0);}
      .pdf-sc.balance-neg{background:linear-gradient(180deg,#fce8e8,#fff5f5);}

      /* ── SECTION ── */
      .pdf-section{margin-bottom:34px;page-break-inside:avoid;}
      .pdf-section-header{display:flex;align-items:stretch;gap:0;margin-bottom:14px;}
      .pdf-sec-accent{width:5px;background:linear-gradient(180deg,#8b1a1a,#c9973a);border-radius:3px;flex-shrink:0;margin-right:12px;}
      .pdf-sec-accent.green{background:linear-gradient(180deg,#2a6b2a,#5a9a5a);}
      .pdf-sec-accent.gold{background:linear-gradient(180deg,#8a6020,#c9973a);}
      .pdf-sec-title-block{flex:1;border-bottom:1.5px solid #e0c070;padding-bottom:8px;}
      .pdf-sec-title{font-family:'Cinzel',serif;font-size:12px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#3d0c0c;}
      .pdf-sec-subtitle{font-family:'EB Garamond',serif;font-size:12px;color:#b8a070;margin-top:3px;}
      .pdf-sec-num{width:28px;height:28px;border-radius:50%;background:#3d0c0c;color:#e8c46a;font-family:'Cinzel',serif;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-left:12px;margin-top:2px;}

      /* ── TABLES ── */
      table{width:100%;border-collapse:collapse;font-size:12px;font-family:'EB Garamond',serif;}
      thead tr{background:linear-gradient(135deg,#2e0c0c,#4a1010);}
      thead th{font-family:'Cinzel',serif;font-size:7.5px;letter-spacing:1.5px;text-transform:uppercase;color:#e8c46a;padding:10px 11px;text-align:left;font-weight:600;border-bottom:2px solid #c9973a;}
      tbody tr{border-bottom:1px solid #f0e8d0;transition:background 0.1s;}
      tbody tr:nth-child(even){background:#fffbf3;}
      tbody td{padding:9px 11px;color:#2c1008;vertical-align:top;line-height:1.45;}
      .td-right{text-align:right;}
      .td-center{text-align:center;}
      .td-num{text-align:center;color:#c9973a;font-family:'Cinzel',serif;font-size:10px;width:26px;font-weight:600;}
      .td-amount{font-family:'Cinzel',serif;font-weight:700;text-align:right;color:#1a5c1a;white-space:nowrap;font-size:12.5px;}
      .td-amount.red{color:#8b1a1a;}
      .td-receipt{font-size:11px;color:#7a5020;font-family:'Cinzel',serif;letter-spacing:0.5px;}
      .td-pill{display:inline-block;font-family:'Cinzel',serif;font-size:7.5px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;padding:3px 8px;border:1px solid rgba(201,151,58,0.35);border-radius:20px;color:#7a5020;background:rgba(201,151,58,0.1);white-space:nowrap;}
      .td-bold{font-weight:600;color:#1a0404;}
      tfoot tr{border-top:2px solid #c9973a;}
      tfoot td{background:linear-gradient(135deg,#3d0c0c,#2e0c0c);font-family:'Cinzel',serif;font-size:11px;font-weight:700;padding:10px 11px;color:#e8c46a;letter-spacing:1px;}
      .tfoot-amt{color:#f5e090;text-align:right;}

      /* ── CATEGORY SUB-HEADER ── */
      .pdf-cat-header{display:flex;align-items:center;gap:10px;margin:18px 0 8px;padding:8px 12px;background:linear-gradient(90deg,rgba(201,151,58,0.1),transparent);border-left:3px solid #c9973a;border-radius:0 4px 4px 0;}
      .pdf-cat-name{font-family:'Cinzel',serif;font-size:11px;font-weight:700;color:#5a3010;letter-spacing:1px;text-transform:uppercase;}
      .pdf-cat-type{font-size:11px;color:#b8a070;font-style:italic;margin-left:4px;}
      .pdf-cat-total{margin-left:auto;font-family:'Cinzel',serif;font-size:11px;font-weight:700;color:#8b1a1a;}

      /* ── BREAKDOWN TABLE ── */
      .pdf-breakdown{margin-top:10px;margin-left:auto;width:48%;border:1px solid #e8d89a;border-radius:4px;overflow:hidden;}
      .pdf-breakdown table thead tr{background:linear-gradient(135deg,#f5e8cc,#ede0b0);}
      .pdf-breakdown table thead th{color:#5a3010;font-size:7px;}
      .pdf-breakdown table tbody td{background:#fff !important;}

      /* ── SUMMARY TABLE ── */
      .pdf-fin-summary{border:1.5px solid #e0c070;border-radius:6px;overflow:hidden;margin-top:4px;}
      .pdf-fs-row{display:flex;align-items:center;justify-content:space-between;padding:12px 20px;border-bottom:1px solid #f0e8d0;}
      .pdf-fs-row:last-child{border-bottom:none;}
      .pdf-fs-label{font-family:'EB Garamond',serif;font-size:14px;color:#4a1a08;display:flex;align-items:center;gap:8px;}
      .pdf-fs-label .dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
      .pdf-fs-val{font-family:'Cinzel',serif;font-size:14px;font-weight:700;}
      .pdf-fs-val.green{color:#1a5c1a;}
      .pdf-fs-val.red{color:#8b1a1a;}
      .pdf-fs-row.gross{background:rgba(42,107,42,0.06);}
      .pdf-fs-row.gross .pdf-fs-label{font-family:'Cinzel',serif;font-size:12px;font-weight:700;color:#1a4a1a;letter-spacing:1px;}
      .pdf-fs-row.gross .pdf-fs-val{font-size:16px;}
      .pdf-fs-row.net-pos{background:linear-gradient(90deg,rgba(42,107,42,0.1),rgba(42,107,42,0.04));border-top:2px solid #2a6b2a;}
      .pdf-fs-row.net-neg{background:linear-gradient(90deg,rgba(139,26,26,0.1),rgba(139,26,26,0.04));border-top:2px solid #8b1a1a;}
      .pdf-fs-row.net-pos .pdf-fs-label,.pdf-fs-row.net-neg .pdf-fs-label{font-family:'Cinzel',serif;font-size:13px;font-weight:700;letter-spacing:1px;}
      .pdf-fs-row.net-pos .pdf-fs-val,.pdf-fs-row.net-neg .pdf-fs-val{font-size:20px;}

      /* ── FOOTER ── */
      .pdf-footer{margin-top:40px;padding:18px 0 0;border-top:1px solid #e0c070;display:flex;align-items:center;justify-content:space-between;gap:20px;}
      .pdf-footer-text{font-family:'EB Garamond',serif;font-size:11px;color:#c0a878;line-height:1.8;font-style:italic;}
      .pdf-footer-seal{text-align:center;}
      .pdf-footer-om{font-size:28px;color:rgba(139,26,26,0.18);}
      .pdf-footer-seal-text{font-family:'Cinzel',serif;font-size:7px;letter-spacing:2px;color:#d4b87a;text-transform:uppercase;margin-top:2px;}

      @media print{
        @page{margin:12mm 14mm;size:A4;}
        body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
        .pdf-section{page-break-inside:avoid;}
        thead{display:table-header-group;}
        tr{page-break-inside:avoid;page-break-after:auto;}
      }
    `;
    const html = printRef.current?.innerHTML || "";
    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Temple Financial Report</title><style>${css}</style></head><body><div class="pdf-wrap">${html}</div></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 900);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const html = printRef.current?.innerHTML || "";
      const css = `
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Noto+Sans+Malayalam:wght@400;700&display=swap');
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:'EB Garamond',serif;background:#fff;color:#1a0404;}
        .pdf-wrap{width:794px;margin:0 auto;background:#fff;}
        .pdf-header{background:linear-gradient(135deg,#2e0c0c 0%,#5a1515 60%,#3d0c0c 100%);padding:28px 40px 22px;position:relative;}
        .pdf-header-gold-bar{position:absolute;bottom:0;left:0;right:0;height:3px;background:#c9973a;}
        .pdf-hdr-row1{display:flex;align-items:center;gap:18px;margin-bottom:14px;}
        .pdf-hdr-seal{width:64px;height:64px;border-radius:50%;background:rgba(232,196,106,0.1);border:2px solid rgba(232,196,106,0.4);display:flex;align-items:center;justify-content:center;font-size:26px;color:#e8c46a;flex-shrink:0;overflow:hidden;}
        .pdf-hdr-logo-img{width:100%;height:100%;object-fit:cover;border-radius:50%;}
        .pdf-hdr-logo-fallback{display:none;font-size:26px;color:#e8c46a;align-items:center;justify-content:center;}
        .pdf-hdr-temple-mal{font-family:'Noto Sans Malayalam',serif;font-size:16px;font-weight:700;color:#fff8e7;line-height:1.4;margin-bottom:2px;}
        .pdf-hdr-temple-name{font-family:'Cinzel',serif;font-size:13px;font-weight:700;color:#e8c46a;letter-spacing:1.5px;}
        .pdf-hdr-temple-sub{font-family:'EB Garamond',serif;font-size:11.5px;color:rgba(232,196,106,0.5);letter-spacing:1px;margin-top:4px;}
        .pdf-hdr-divider{height:1px;background:rgba(232,196,106,0.3);margin:0 0 12px;}
        .pdf-hdr-row2{display:flex;align-items:flex-end;justify-content:space-between;}
        .pdf-hdr-report-label{font-family:'Cinzel',serif;font-size:9px;font-weight:600;letter-spacing:3px;text-transform:uppercase;color:rgba(232,196,106,0.55);margin-bottom:4px;}
        .pdf-hdr-report-title{font-family:'Cinzel',serif;font-size:17px;font-weight:700;color:#e8c46a;letter-spacing:1.5px;}
        .pdf-hdr-period{font-family:'EB Garamond',serif;font-size:12.5px;color:rgba(232,196,106,0.75);margin-top:4px;font-style:italic;}
        .pdf-hdr-meta{text-align:right;}
        .pdf-hdr-meta-line{font-family:'Cinzel',serif;font-size:9px;color:rgba(232,196,106,0.42);letter-spacing:1px;line-height:1.9;}
        .pdf-body{padding:24px 40px 40px;}
        .pdf-summary-strip{display:grid;grid-template-columns:repeat(5,1fr);border:1.5px solid #e0c070;border-radius:5px;overflow:hidden;margin-bottom:28px;}
        .pdf-sc{padding:12px 10px;text-align:center;border-right:1px solid #e8d89a;background:#fff;}
        .pdf-sc:last-child{border-right:none;}
        .pdf-sc-label{font-family:'Cinzel',serif;font-size:6.5px;letter-spacing:1.5px;text-transform:uppercase;color:#8a6020;margin-bottom:6px;}
        .pdf-sc-val{font-family:'Cinzel',serif;font-size:13px;font-weight:700;}
        .pdf-sc-val.green{color:#1a5c1a;} .pdf-sc-val.red{color:#8b1a1a;}
        .pdf-sc-note{font-family:'EB Garamond',serif;font-size:10px;color:#b8a070;margin-top:3px;}
        .pdf-sc.income{background:#f6fff6;} .pdf-sc.expense{background:#fff6f6;}
        .pdf-sc.balance-pos{background:#edf7ed;} .pdf-sc.balance-neg{background:#fceaea;}
        .pdf-section{margin-bottom:26px;}
        .pdf-section-header{display:flex;align-items:stretch;margin-bottom:11px;}
        .pdf-sec-accent{width:4px;background:#8b1a1a;border-radius:3px;flex-shrink:0;margin-right:11px;}
        .pdf-sec-accent.green{background:#2a6b2a;} .pdf-sec-accent.gold{background:#8a6020;}
        .pdf-sec-title-block{flex:1;border-bottom:1.5px solid #e0c070;padding-bottom:7px;}
        .pdf-sec-title{font-family:'Cinzel',serif;font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#3d0c0c;}
        .pdf-sec-subtitle{font-family:'EB Garamond',serif;font-size:11.5px;color:#b8a070;margin-top:2px;}
        table{width:100%;border-collapse:collapse;font-size:11px;font-family:'EB Garamond',serif;}
        thead tr{background:#2e0c0c;}
        thead th{font-family:'Cinzel',serif;font-size:7px;letter-spacing:1.5px;text-transform:uppercase;color:#e8c46a;padding:9px 10px;text-align:left;font-weight:600;border-bottom:2px solid #c9973a;}
        tbody tr{border-bottom:1px solid #f0e8d0;} tbody tr:nth-child(even){background:#fffbf3;}
        tbody td{padding:8px 10px;color:#2c1008;vertical-align:top;line-height:1.4;}
        .td-center{text-align:center;} .td-right{text-align:right;}
        .td-num{text-align:center;color:#c9973a;font-family:'Cinzel',serif;font-size:9px;width:22px;font-weight:600;}
        .td-amount{font-family:'Cinzel',serif;font-weight:700;text-align:right;color:#1a5c1a;white-space:nowrap;font-size:11.5px;}
        .td-amount.red{color:#8b1a1a;}
        .td-receipt{font-size:10px;color:#7a5020;font-family:'Cinzel',serif;letter-spacing:0.5px;}
        .td-pill{display:inline-block;font-family:'Cinzel',serif;font-size:6.5px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;padding:2px 7px;border:1px solid rgba(201,151,58,0.35);border-radius:20px;color:#7a5020;background:rgba(201,151,58,0.1);white-space:nowrap;}
        .td-bold{font-weight:600;color:#1a0404;}
        tfoot tr{border-top:2px solid #c9973a;}
        tfoot td{background:#3d0c0c;font-family:'Cinzel',serif;font-size:10px;font-weight:700;padding:9px 10px;color:#e8c46a;letter-spacing:1px;}
        .tfoot-amt{color:#f5e090;text-align:right;}
        .pdf-cat-header{display:flex;align-items:center;gap:10px;margin:14px 0 7px;padding:7px 11px;background:rgba(201,151,58,0.08);border-left:3px solid #c9973a;border-radius:0 4px 4px 0;}
        .pdf-cat-name{font-family:'Cinzel',serif;font-size:10px;font-weight:700;color:#5a3010;letter-spacing:1px;text-transform:uppercase;}
        .pdf-cat-type{font-size:10px;color:#b8a070;font-style:italic;margin-left:4px;}
        .pdf-cat-total{margin-left:auto;font-family:'Cinzel',serif;font-size:10px;font-weight:700;color:#8b1a1a;}
        .pdf-breakdown{margin-top:10px;margin-left:auto;width:46%;border:1px solid #e8d89a;border-radius:4px;overflow:hidden;}
        .pdf-breakdown table thead tr{background:#f5e8cc;}
        .pdf-breakdown table thead th{color:#5a3010;font-size:6.5px;}
        .pdf-breakdown table tbody td{background:#fff !important;}
        .pdf-fin-summary{border:1.5px solid #e0c070;border-radius:5px;overflow:hidden;margin-top:4px;}
        .pdf-fs-row{display:flex;align-items:center;justify-content:space-between;padding:11px 18px;border-bottom:1px solid #f0e8d0;background:#fff;}
        .pdf-fs-row:last-child{border-bottom:none;}
        .pdf-fs-label{font-family:'EB Garamond',serif;font-size:13px;color:#4a1a08;display:flex;align-items:center;gap:8px;}
        .pdf-fs-label .dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
        .pdf-fs-val{font-family:'Cinzel',serif;font-size:13px;font-weight:700;}
        .pdf-fs-val.green{color:#1a5c1a;} .pdf-fs-val.red{color:#8b1a1a;}
        .pdf-fs-row.gross{background:#f2faf2;}
        .pdf-fs-row.gross .pdf-fs-label{font-family:'Cinzel',serif;font-size:11px;font-weight:700;color:#1a4a1a;letter-spacing:1px;}
        .pdf-fs-row.gross .pdf-fs-val{font-size:15px;}
        .pdf-fs-row.net-pos{background:#e8f5e8;border-top:2px solid #2a6b2a;}
        .pdf-fs-row.net-neg{background:#fce8e8;border-top:2px solid #8b1a1a;}
        .pdf-fs-row.net-pos .pdf-fs-label,.pdf-fs-row.net-neg .pdf-fs-label{font-family:'Cinzel',serif;font-size:12px;font-weight:700;letter-spacing:1px;}
        .pdf-fs-row.net-pos .pdf-fs-val,.pdf-fs-row.net-neg .pdf-fs-val{font-size:18px;}
        .pdf-footer{margin-top:30px;padding:16px 0 0;border-top:1px solid #e0c070;display:flex;align-items:center;justify-content:space-between;gap:20px;}
        .pdf-footer-text{font-family:'EB Garamond',serif;font-size:10.5px;color:#c0a878;line-height:1.8;font-style:italic;}
        .pdf-footer-seal{text-align:center;}
        .pdf-footer-om{font-size:26px;color:rgba(139,26,26,0.18);}
        .pdf-footer-seal-text{font-family:'Cinzel',serif;font-size:6.5px;letter-spacing:2px;color:#d4b87a;text-transform:uppercase;margin-top:2px;}
      `;

      const filename = `Temple_Financial_Report_${periodLabel.replace(/[^a-zA-Z0-9]/g,"_")}_${new Date().toISOString().slice(0,10)}.pdf`;

      // Load libraries if needed
      const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement("script");
        s.src = src; s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
      });

      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

      // Build hidden container with only body-level content (no full HTML doc string)
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;width:794px;background:#fff;z-index:-1;";

      const styleEl = document.createElement("style");
      styleEl.textContent = css;

      const contentDiv = document.createElement("div");
      contentDiv.className = "pdf-wrap";
      contentDiv.innerHTML = html;

      container.appendChild(styleEl);
      container.appendChild(contentDiv);
      document.body.appendChild(container);

      // Wait for fonts/images to settle
      await new Promise(r => setTimeout(r, 600));

      const canvas = await window.html2canvas(contentDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        // Ignore zero-size elements that cause createPattern crash
        onclone: (clonedDoc) => {
          clonedDoc.querySelectorAll("*").forEach(el => {
            const s = el.style;
            // Replace CSS gradients on pseudo-element-like thin decorators with solid colours
            if (s.background && s.background.includes("gradient")) {
              const rect = el.getBoundingClientRect();
              if (rect.width === 0 || rect.height === 0) {
                s.background = "transparent";
              }
            }
          });
        }
      });

      document.body.removeChild(container);

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

      const pageW = pdf.internal.pageSize.getWidth();   // 210mm
      const pageH = pdf.internal.pageSize.getHeight();  // 297mm
      const margin = 10; // mm
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      let yPos = margin;
      let remainingH = imgH;

      // Slice the image across pages
      while (remainingH > 0) {
        const sliceH = Math.min(remainingH, pageH - margin * 2);
        const srcY = (imgH - remainingH) * (canvas.height / imgH);
        const srcH = sliceH * (canvas.height / imgH);

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = srcH;
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

        const pageData = pageCanvas.toDataURL("image/jpeg", 0.95);
        pdf.addImage(pageData, "JPEG", margin, yPos, imgW, sliceH);

        remainingH -= sliceH;
        if (remainingH > 0) { pdf.addPage(); yPos = margin; }
      }

      pdf.save(filename);

    } catch (err) {
      console.error("PDF download error:", err);
      alert("PDF download failed: " + (err?.message || String(err)));
    } finally {
      setDownloading(false);
    }
  };

  return createPortal(
    <div className="rpt-modal-overlay" onClick={onClose}>
      <div className="rpt-pdf-modal" onClick={e => e.stopPropagation()}>

        {/* ── MODAL TOOLBAR ── */}
        <div className="rpt-pdf-modal-header">
          <div className="rpt-pdf-modal-title">
            <div className="rpt-pdf-hicon-wrap"><FaFilePdf /></div>
            <div>
              <div className="rpt-pdf-modal-eyebrow">✦ {t("rptPdfPreviewTitle")} ✦</div>
              <div className="rpt-pdf-modal-name">{t("rptPdfFinancialReport")} — {periodLabel}</div>
            </div>
          </div>
          <div className="rpt-pdf-modal-actions">
            {/* Print */}
            <button className="rpt-pdf-action-btn rpt-pdf-print" onClick={handlePrint}>
              <FaPrint /> {t("rptPdfPrintBtn")}
            </button>
            {/* Download PDF */}
            <button className="rpt-pdf-action-btn rpt-pdf-download" onClick={handleDownload} disabled={downloading}>
              {downloading
                ? <><span className="rpt-pdf-spin">◌</span> {t("loading") || "Downloading…"}</>
                : <><FaDownload /> {t("rptBtnDownloadPdf") || "Download PDF"}</>}
            </button>
            <button className="rpt-pdf-action-btn rpt-pdf-close-btn" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>

        {/* ── SCROLLABLE CANVAS (grey bg simulates print margins) ── */}
        <div className="rpt-pdf-scroll">
          <div className="rpt-pdf-canvas">
            <div className="rpt-pdf-paper" ref={printRef}>

              {/* ══ DOCUMENT HEADER ══ */}
              <div className="pdf-header">
                <div className="pdf-header-gold-bar" />
                <div className="pdf-hdr-row1">
                  <div className="pdf-hdr-seal">
                    <img src="./logo.png" alt="Temple Logo" className="pdf-hdr-logo-img"
                      onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                    />
                    <span className="pdf-hdr-logo-fallback">ॐ</span>
                  </div>
                  <div>
                    <div className="pdf-hdr-temple-mal">കൊടുങ്ങല്ലൂർ കാവിൽ ശ്രീ ഭദ്രകാളി ദേവി ക്ഷേത്രം</div>
                    <div className="pdf-hdr-temple-name">Kodungallur Kavil Sree Bhadrakali Devi Temple</div>
                    <div className="pdf-hdr-temple-sub">പാണ്ടനാട് · ചെങ്ങന്നൂർ &nbsp;·&nbsp; Pandanad · Chengannur</div>
                  </div>
                </div>
                <div className="pdf-hdr-divider" />
                <div className="pdf-hdr-row2">
                  <div>
                    <div className="pdf-hdr-report-label">{t("rptPdfFinancialReport")}</div>
                    <div className="pdf-hdr-report-title">{t("rptPdfStatementOfAccounts")}</div>
                    <div className="pdf-hdr-period">{t("rptPdfPeriod")}: {periodLabel}</div>
                  </div>
                  <div className="pdf-hdr-meta">
                    <div className="pdf-hdr-meta-line">{t("rptPdfGeneratedOn")}</div>
                    <div className="pdf-hdr-meta-line" style={{fontSize:"12px",color:"rgba(232,196,106,0.75)",fontFamily:"'Cinzel',serif"}}>
                      {new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}
                    </div>
                    <div className="pdf-hdr-meta-line">{new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</div>
                  </div>
                </div>
              </div>

              {/* ══ DOCUMENT BODY ══ */}
              <div className="pdf-body">

                {/* ── SUMMARY STRIP ── */}
                <div className="pdf-summary-strip">
                  <div className="pdf-sc income">
                    <div className="pdf-sc-label">{t("rptStatDonations")}</div>
                    <div className="pdf-sc-val green">{fmtAmt(totalDonations)}</div>
                    <div className="pdf-sc-note">{donations.length} {t("rptPdfRecords")}</div>
                  </div>
                  <div className="pdf-sc income">
                    <div className="pdf-sc-label">{t("rptStatOfferings")}</div>
                    <div className="pdf-sc-val green">{fmtAmt(totalBookings)}</div>
                    <div className="pdf-sc-note">{bookings.length} {t("rptPdfBookings")}</div>
                  </div>
                  <div className="pdf-sc income">
                    <div className="pdf-sc-label">{t("rptStatTotalIncome")}</div>
                    <div className="pdf-sc-val green" style={{fontSize:"16px"}}>{fmtAmt(totalIncome)}</div>
                    <div className="pdf-sc-note">{t("rptPdfCombined")}</div>
                  </div>
                  <div className="pdf-sc expense">
                    <div className="pdf-sc-label">{t("rptStatTotalExpenses")}</div>
                    <div className="pdf-sc-val red">{fmtAmt(totalExpenses)}</div>
                    <div className="pdf-sc-note">{expenses.length} {t("rptPdfEntries")}</div>
                  </div>
                  <div className={`pdf-sc ${netBalance >= 0 ? "balance-pos" : "balance-neg"}`}>
                    <div className="pdf-sc-label">{t("rptStatNetBalance")}</div>
                    <div className={`pdf-sc-val ${netBalance >= 0 ? "green" : "red"}`} style={{fontSize:"16px"}}>{fmtAmt(netBalance)}</div>
                    <div className="pdf-sc-note">{netBalance >= 0 ? t("rptPdfSurplusLabel") : t("rptPdfDeficitLabel")}</div>
                  </div>
                </div>

                {/* ── SECTION 1: DONATIONS ── */}
                {donations.length > 0 && (
                  <div className="pdf-section">
                    <div className="pdf-section-header">
                      <div className="pdf-sec-accent green" />
                      <div className="pdf-sec-title-block">
                        <div className="pdf-sec-title">{t("rptPdfSection1")}</div>
                        <div className="pdf-sec-subtitle">{donations.length} {t("rptPdfRecords")} · {t("rptPdfTotal")}: {fmtAmt(totalDonations)}</div>
                      </div>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th style={{width:"28px"}}>#</th>
                          <th>{t("rptColDate")}</th>
                          <th>{t("rptColReceipt")}</th>
                          <th>{t("rptColDonorName")}</th>
                          <th>{t("rptColMobile")}</th>
                          <th>{t("rptColType")}</th>
                          <th>{t("donColMode")}</th>
                          <th style={{textAlign:"right"}}>{t("rptColAmount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donations.map((d, i) => (
                          <tr key={d.donation_id}>
                            <td className="td-num">{i + 1}</td>
                            <td>{fmtDate(d.created_at)}</td>
                            <td className="td-receipt">{d.receipt_number || "—"}</td>
                            <td className="td-bold">{d.name}</td>
                            <td>{d.mobile_number}</td>
                            <td><span className="td-pill">{d.donation_type || t("donTypeOther")}</span></td>
                            <td>{d.payment_mode || "cash"}</td>
                            <td className="td-amount">{fmtAmt(d.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="7" style={{textAlign:"right"}}>{t("rptTotalDonations")}</td>
                          <td className="tfoot-amt">{fmtAmt(totalDonations)}</td>
                        </tr>
                      </tfoot>
                    </table>
                    {Object.keys(donByType).length > 1 && (
                      <div className="pdf-breakdown">
                        <table>
                          <thead><tr><th>{t("rptColType")}</th><th>{t("statTotal")}</th><th style={{textAlign:"right"}}>{t("rptPdfTotal")}</th></tr></thead>
                          <tbody>
                            {Object.entries(donByType).map(([k, v]) => (
                              <tr key={k}>
                                <td><span className="td-pill">{k}</span></td>
                                <td className="td-center">{v.count}</td>
                                <td className="td-amount">{fmtAmt(v.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* ── SECTION 2: OFFERINGS ── */}
                {bookings.length > 0 && (
                  <div className="pdf-section">
                    <div className="pdf-section-header">
                      <div className="pdf-sec-accent gold" />
                      <div className="pdf-sec-title-block">
                        <div className="pdf-sec-title">{t("rptPdfSection2")}</div>
                        <div className="pdf-sec-subtitle">{bookings.length} {t("rptPdfBookings")} · {t("rptPdfTotal")}: {fmtAmt(totalBookings)}</div>
                      </div>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th style={{width:"28px"}}>#</th>
                          <th>{t("rptColDate")}</th>
                          <th>{t("rptColReceipt")}</th>
                          <th>{t("rptColMobile")}</th>
                          <th>{t("rptColVazhipadu")}</th>
                          <th>{t("rptColDevoteeName")}</th>
                          <th>{t("rptColPersons")}</th>
                          <th style={{textAlign:"right"}}>{t("rptColAmount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map((b, i) => (
                          <tr key={b.booking_id}>
                            <td className="td-num">{i + 1}</td>
                            <td>{fmtDate(b.booking_date || b.created_at)}</td>
                            <td className="td-receipt">{b.receipt_number || "—"}</td>
                            <td>{b.mobile_number}</td>
                            <td><span className="td-pill">{b.category_name || b.vazhipadu_name || "—"}</span></td>
                            <td className="td-bold">
                              {b.devotee_name || b.name ||
                               (Array.isArray(b.persons) && b.persons.length > 0
                                 ? b.persons.map(p => p.name).join(", ")
                                 : "—")}
                            </td>
                            <td className="td-center">{b.total_persons || 1}</td>
                            <td className="td-amount">{fmtAmt(b.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="7" style={{textAlign:"right"}}>{t("rptTotalOfferings")}</td>
                          <td className="tfoot-amt">{fmtAmt(totalBookings)}</td>
                        </tr>
                      </tfoot>
                    </table>
                    {Object.keys(bkByType).length > 1 && (
                      <div className="pdf-breakdown">
                        <table>
                          <thead><tr><th>{t("vazhipadu")}</th><th>{t("rptPdfBookings")}</th><th style={{textAlign:"right"}}>{t("rptPdfTotal")}</th></tr></thead>
                          <tbody>
                            {Object.entries(bkByType).map(([k, v]) => (
                              <tr key={k}>
                                <td><span className="td-pill">{k}</span></td>
                                <td className="td-center">{v.count}</td>
                                <td className="td-amount">{fmtAmt(v.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* ── SECTION 3: EXPENSES (grouped by category) ── */}
                {expenses.length > 0 && (
                  <div className="pdf-section">
                    <div className="pdf-section-header">
                      <div className="pdf-sec-accent" />
                      <div className="pdf-sec-title-block">
                        <div className="pdf-sec-title">{t("rptPdfSection3")}</div>
                        <div className="pdf-sec-subtitle">{expenses.length} {t("rptPdfEntries")} · {t("rptPdfTotal")}: {fmtAmt(totalExpenses)}</div>
                      </div>
                    </div>
                    {Object.entries(expByCat).map(([cat, { items, type }]) => {
                      const catTotal = items.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
                      return (
                        <div key={cat} style={{marginBottom:"18px"}}>
                          <div className="pdf-cat-header">
                            <div className="pdf-cat-name">{cat}</div>
                            {type && <div className="pdf-cat-type">({type})</div>}
                            <div className="pdf-cat-total">{fmtAmt(catTotal)}</div>
                          </div>
                          <table>
                            <thead>
                              <tr>
                                <th style={{width:"28px"}}>#</th>
                                <th>{t("rptColDate")}</th>
                                <th>{t("rptColTitle")}</th>
                                <th style={{textAlign:"right"}}>{t("rptColAmount")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((e, i) => (
                                <tr key={e.expense_id}>
                                  <td className="td-num">{i + 1}</td>
                                  <td>{fmtDate(e.expense_date)}</td>
                                  <td className="td-bold">{e.title}</td>
                                  <td className="td-amount red">{fmtAmt(e.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan="3" style={{textAlign:"right",background:"#f9f0e0",color:"#5a3010",fontFamily:"'Cinzel',serif",fontSize:"10px",fontWeight:"700",padding:"8px 11px",borderTop:"1px solid #d4a843"}}>
                                  {t("rptPdfTotal")} — {cat}
                                </td>
                                <td className="td-amount red" style={{background:"#f9f0e0",padding:"8px 11px",borderTop:"1px solid #d4a843",fontFamily:"'Cinzel',serif",fontWeight:"700"}}>
                                  {fmtAmt(catTotal)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      );
                    })}
                    <table>
                      <tfoot>
                        <tr>
                          <td style={{textAlign:"right",padding:"11px 14px"}}>{t("rptTotalExpenses")}</td>
                          <td className="tfoot-amt" style={{padding:"11px 14px",textAlign:"right",color:"#f5b8b8"}}>{fmtAmt(totalExpenses)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* ── SECTION 4: FINANCIAL SUMMARY ── */}
                <div className="pdf-section">
                  <div className="pdf-section-header">
                    <div className="pdf-sec-accent" style={{background: netBalance >= 0 ? "linear-gradient(180deg,#2a6b2a,#5a9a5a)" : "linear-gradient(180deg,#8b1a1a,#c94040)"}} />
                    <div className="pdf-sec-title-block">
                      <div className="pdf-sec-title">{t("rptPdfSection4")}</div>
                      <div className="pdf-sec-subtitle">{t("rptPdfPeriod")}: {periodLabel}</div>
                    </div>
                  </div>
                  <div className="pdf-fin-summary">
                    <div className="pdf-fs-row">
                      <div className="pdf-fs-label">
                        <span className="dot" style={{background:"#2a6b2a"}} />
                        {t("rptPdfIncomeFromDonations")}
                        <span style={{fontSize:"11px",color:"#b8a070",marginLeft:"6px"}}>({donations.length} {t("rptPdfRecords")})</span>
                      </div>
                      <div className="pdf-fs-val green">{fmtAmt(totalDonations)}</div>
                    </div>
                    <div className="pdf-fs-row">
                      <div className="pdf-fs-label">
                        <span className="dot" style={{background:"#5a9a2a"}} />
                        {t("rptPdfIncomeFromOfferings")}
                        <span style={{fontSize:"11px",color:"#b8a070",marginLeft:"6px"}}>({bookings.length} {t("rptPdfBookings")})</span>
                      </div>
                      <div className="pdf-fs-val green">{fmtAmt(totalBookings)}</div>
                    </div>
                    <div className="pdf-fs-row gross">
                      <div className="pdf-fs-label">{t("rptPdfGrossIncome")}</div>
                      <div className="pdf-fs-val green">{fmtAmt(totalIncome)}</div>
                    </div>
                    <div className="pdf-fs-row">
                      <div className="pdf-fs-label">
                        <span className="dot" style={{background:"#8b1a1a"}} />
                        {t("rptPdfTotalExpenses")}
                        <span style={{fontSize:"11px",color:"#b8a070",marginLeft:"6px"}}>({expenses.length} {t("rptPdfEntries")})</span>
                      </div>
                      <div className="pdf-fs-val red">{fmtAmt(totalExpenses)}</div>
                    </div>
                    <div className={`pdf-fs-row ${netBalance >= 0 ? "net-pos" : "net-neg"}`}>
                      <div className="pdf-fs-label">
                        {t("rptPdfNetBalance")} {netBalance >= 0 ? `(${t("rptPdfSurplus")})` : `(${t("rptPdfDeficit")})`}
                      </div>
                      <div className={`pdf-fs-val ${netBalance >= 0 ? "green" : "red"}`}>
                        {fmtAmt(Math.abs(netBalance))}
                      </div>
```
                    </div>
                  </div>
                </div>

                {/* ── DOCUMENT FOOTER ── */}
                <div className="pdf-footer">
                  <div className="pdf-footer-text">
                    {t("rptPdfFooterNote")}<br />
                    {t("rptPdfFooterNote2")} {periodLabel}.
                  </div>
                  <div className="pdf-footer-seal">
                    <div className="pdf-footer-om">ॐ</div>
                    <div className="pdf-footer-seal-text">{t("rptPdfTempleRecords")}</div>
                  </div>
                </div>

              </div>{/* end pdf-body */}
            </div>{/* end pdf-paper */}
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT: Areports.js
═══════════════════════════════════════════════════ */
export default function AReports() {
  const { t, i18n } = useTranslation();

  // ── LANGUAGE TOGGLE ──
  const currentLang = i18n.language?.startsWith("ml") ? "ml" : "en";
  const toggleLang = () => i18n.changeLanguage(currentLang === "en" ? "ml" : "en");

  const [donations, setDonations]                   = useState([]);
  const [bookings, setBookings]                     = useState([]);
  const [expenses, setExpenses]                     = useState([]);
  const [expenseCategories, setExpenseCategories]   = useState([]);
  const [loading, setLoading]                       = useState(false);
  const [logoFace, setLogoFace]                     = useState("om");

  // Filter States
  const [filterMode, setFilterMode]         = useState("month");
  const [selectedMonth, setSelectedMonth]   = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedYear, setSelectedYear]     = useState(String(new Date().getFullYear()));
  const [specificDate, setSpecificDate]     = useState("");
  const [dateRange, setDateRange]           = useState({ start: "", end: "" });
  const [donationTypeFilter, setDonationTypeFilter] = useState("all");
  const [expCategoryFilter, setExpCategoryFilter]   = useState("all");
  const [sourceFilter, setSourceFilter]             = useState("all");
  const [showFilterPanel, setShowFilterPanel]       = useState(false);

  // UI
  const [toast, setToast]   = useState(null);
  const [pdfModal, setPdfModal] = useState(false);

  const showToast = (msg, type = "info") => setToast({ message: msg, type });

  // OM/Logo flip
  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  // ── FETCH ALL DATA ──
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, bRes, eRes, ecRes] = await Promise.all([
        fetch(`${API}/api/admin/donations`),
        fetch(`${API}/api/admin/bookings`),
        fetch(`${API}/api/admin/expenses`),
        fetch(`${API}/api/admin/expense-categories`),
      ]);
      const [d, b, e, ec] = await Promise.all([dRes.json(), bRes.json(), eRes.json(), ecRes.json()]);
      setDonations(Array.isArray(d) ? d : []);
      setBookings(Array.isArray(b) ? b : []);
      setExpenses(Array.isArray(e) ? e : []);
      setExpenseCategories(Array.isArray(ec) ? ec : []);
    } catch (err) {
      showToast("Failed to load report data", "error");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── DATE HELPER ──
  const toDateStr = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    if (!s.endsWith("Z")) return s.slice(0, 10);
    const d = new Date(s);
    if (isNaN(d)) return s.slice(0, 10);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  };

  const inDateFilter = (dateVal) => {
    const ds = toDateStr(dateVal);
    if (!ds) return false;
    if (filterMode === "specificDate" && specificDate) return ds === specificDate;
    if (filterMode === "month"  && selectedMonth) return ds.startsWith(selectedMonth);
    if (filterMode === "year"   && selectedYear)  return ds.startsWith(selectedYear);
    if (filterMode === "dateRange") {
      if (dateRange.start && dateRange.end) return ds >= dateRange.start && ds <= dateRange.end;
      if (dateRange.start) return ds >= dateRange.start;
      if (dateRange.end)   return ds <= dateRange.end;
      return true;
    }
    return true; // "all"
  };

  // ── FILTERED SETS ──
  const filteredDonations = donations.filter(d => {
    if (!inDateFilter(d.created_at)) return false;
    if (donationTypeFilter !== "all" && d.donation_type !== donationTypeFilter) return false;
    if (sourceFilter === "online"  && d.booked_by !== "online") return false;
    if (sourceFilter === "offline" && d.booked_by !== "admin")  return false;
    return d.payment_status === "success";
  });

  const filteredBookings = bookings.filter(b => {
    if (!inDateFilter(b.booking_date || b.created_at)) return false;
    if (sourceFilter === "online"  && b.booked_by !== "online") return false;
    if (sourceFilter === "offline" && b.booked_by !== "admin")  return false;
    return b.payment_status === "success";
  });

  const filteredExpenses = expenses.filter(e => {
    if (!inDateFilter(e.expense_date || e.created_at)) return false;
    if (expCategoryFilter !== "all" && String(e.category_id) !== String(expCategoryFilter)) return false;
    return true;
  });

  // ── TOTALS ──
  const totalDonations = filteredDonations.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
  const totalBookings  = filteredBookings.reduce((s, b)  => s + parseFloat(b.amount  || 0), 0);
  const totalIncome    = totalDonations + totalBookings;
  const totalExpenses  = filteredExpenses.reduce((s, e)  => s + parseFloat(e.amount  || 0), 0);
  const netBalance     = totalIncome - totalExpenses;

  const fmtAmt = (n) => `₹${parseFloat(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d) ? v : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const activeFilters =
    (filterMode !== "all" ? 1 : 0) +
    (donationTypeFilter !== "all" ? 1 : 0) +
    (expCategoryFilter !== "all" ? 1 : 0) +
    (sourceFilter !== "all" ? 1 : 0);

  const clearFilters = () => {
    setFilterMode("month");
    const n = new Date();
    setSelectedMonth(`${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`);
    setSelectedYear(String(n.getFullYear()));
    setSpecificDate(""); setDateRange({ start: "", end: "" });
    setDonationTypeFilter("all"); setExpCategoryFilter("all"); setSourceFilter("all");
  };

  const periodLabel = (() => {
    if (filterMode === "specificDate" && specificDate)
      return new Date(specificDate).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    if (filterMode === "month" && selectedMonth) {
      const [y, m] = selectedMonth.split("-");
      return new Date(y, m-1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    }
    if (filterMode === "year") return `Year ${selectedYear}`;
    if (filterMode === "dateRange") {
      const s = dateRange.start ? new Date(dateRange.start).toLocaleDateString("en-IN") : "—";
      const e = dateRange.end   ? new Date(dateRange.end).toLocaleDateString("en-IN")   : "—";
      return `${s} → ${e}`;
    }
    return t("rptFilterAllTime");
  })();

  const DONATION_TYPES = ["Monthly Contribution", "Annual Pooja Contribution", "Other"];
  const YEARS = Array.from({ length: 6 }, (_, i) => String(new Date().getFullYear() - i));

  return (
    <>
      <AreportsStyles />

      {/* ── HERO ── */}
      <div className="rpt-hero">
        <div className="rpt-hero-bg" />
        <div className="rpt-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="rpt-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>
        <div className="rpt-hero-medallion">
          <div className="rpt-hero-med-ring" />
          <div className="rpt-hero-med-ring rpt-hero-med-ring-2" />
          <div className={`rpt-hero-face ${logoFace === "om" ? "visible" : ""}`}>
            <span className="rpt-hero-om">ॐ</span>
          </div>
          <div className={`rpt-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple" className="rpt-hero-logo-img" />
          </div>
          <div className="rpt-hero-med-halo" />
        </div>
        <div className="rpt-hero-text">
          <div className="rpt-hero-eyebrow">✦ {t("rptTempleRecords")} ✦</div>
          <h1 className="rpt-hero-title">{t("rptPageTitle")}</h1>
          <p className="rpt-hero-sub">{t("rptPageSub")}</p>
          {!loading && <div className="rpt-period-chip"><FaCalendarAlt /> {periodLabel}</div>}
        </div>
        <div className="rpt-hero-rule" />

        {/* Stat Row */}
        <div className="rpt-stat-row">
          <div className="rpt-stat-card" style={{ "--sc": "#2a6b2a" }}>
            <FaHandHoldingHeart className="rpt-stat-icon" />
            <div className="rpt-stat-val">{fmtAmt(totalDonations)}</div>
            <div className="rpt-stat-label">{t("rptStatDonations")}</div>
          </div>
          <div className="rpt-stat-card" style={{ "--sc": "#c9973a" }}>
            <FaPray className="rpt-stat-icon" />
            <div className="rpt-stat-val">{fmtAmt(totalBookings)}</div>
            <div className="rpt-stat-label">{t("rptStatOfferings")}</div>
          </div>
          <div className="rpt-stat-card" style={{ "--sc": "#1a7a35" }}>
            <FaArrowUp className="rpt-stat-icon" />
            <div className="rpt-stat-val">{fmtAmt(totalIncome)}</div>
            <div className="rpt-stat-label">{t("rptStatTotalIncome")}</div>
          </div>
          <div className="rpt-stat-card" style={{ "--sc": "#8b1a1a" }}>
            <FaArrowDown className="rpt-stat-icon" />
            <div className="rpt-stat-val">{fmtAmt(totalExpenses)}</div>
            <div className="rpt-stat-label">{t("rptStatTotalExpenses")}</div>
          </div>
          <div className="rpt-stat-card" style={{ "--sc": netBalance >= 0 ? "#1a5a2a" : "#8b1a1a" }}>
            <FaBalanceScale className="rpt-stat-icon" />
            <div className="rpt-stat-val">{fmtAmt(netBalance)}</div>
            <div className="rpt-stat-label">{t("rptStatNetBalance")}</div>
          </div>
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="rpt-toolbar">
        <button className="rpt-filter-trigger-btn" onClick={() => setShowFilterPanel(v => !v)}>
          <FaFilter /> {t("rptBtnFilters")}
          {activeFilters > 0 && <span className="rpt-filter-badge">{activeFilters}</span>}
          <FaChevronDown className={`rpt-dd-chevron ${showFilterPanel ? "open" : ""}`} />
        </button>
        <div className="rpt-toolbar-btns">
          <button className="rpt-action-btn rpt-btn-pdf" onClick={() => setPdfModal(true)} disabled={loading}>
            <FaFilePdf /> {t("rptBtnPreviewPdf")}
          </button>
          <button className="rpt-action-btn rpt-btn-refresh" onClick={fetchAll}>
            <FaSync className={loading ? "spin" : ""} /> {t("rptBtnRefresh")}
          </button>
          {activeFilters > 0 && (
            <button className="rpt-action-btn rpt-btn-clear" onClick={clearFilters}>
              <FaTimes /> {t("rptBtnClear")}
            </button>
          )}
        </div>
      </div>

      {/* ── FILTER PANEL ── */}
      {showFilterPanel && (
        <div className="rpt-filter-panel">
          <div className="rpt-filter-row rpt-filter-row-full">
            <div className="rpt-filter-item" style={{ flex: "0 0 100%" }}>
              <label className="rpt-filter-label"><FaCalendarAlt /> {t("rptFilterPeriod")}</label>
              <div className="rpt-mode-tabs">
                {[
                  { key: "month",        label: t("rptFilterByMonth") },
                  { key: "year",         label: t("rptFilterByYear") },
                  { key: "dateRange",    label: t("rptFilterDateRange") },
                  { key: "specificDate", label: t("rptFilterSpecificDate") },
                  { key: "all",          label: t("rptFilterAllTime") },
                ].map(({ key, label }) => (
                  <button key={key} className={`rpt-mode-tab ${filterMode === key ? "active" : ""}`} onClick={() => setFilterMode(key)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="rpt-filter-row">
            {filterMode === "month" && (
              <div className="rpt-filter-item">
                <label className="rpt-filter-label">{t("rptFilterByMonth")}</label>
                <input type="month" className="rpt-filter-input" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} />
              </div>
            )}
            {filterMode === "year" && (
              <div className="rpt-filter-item">
                <label className="rpt-filter-label">{t("rptFilterByYear")}</label>
                <select className="rpt-filter-select" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}
            {filterMode === "specificDate" && (
              <div className="rpt-filter-item">
                <label className="rpt-filter-label">{t("rptFilterSpecificDate")}</label>
                <input type="date" className="rpt-filter-input" value={specificDate} onChange={e => setSpecificDate(e.target.value)} />
              </div>
            )}
            {filterMode === "dateRange" && (
              <div className="rpt-filter-item">
                <label className="rpt-filter-label">{t("rptFilterDateRange")}</label>
                <div className="rpt-range-inputs">
                  <input type="date" className="rpt-filter-input" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} />
                  <span className="rpt-range-sep">→</span>
                  <input type="date" className="rpt-filter-input" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} />
                </div>
              </div>
            )}

            <div className="rpt-filter-item">
              <label className="rpt-filter-label"><FaHandHoldingHeart /> {t("rptFilterDonationType")}</label>
              <select className="rpt-filter-select" value={donationTypeFilter} onChange={e => setDonationTypeFilter(e.target.value)}>
                <option value="all">{t("rptFilterAllTypes")}</option>
                {DONATION_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>

            <div className="rpt-filter-item">
              <label className="rpt-filter-label"><FaTags /> {t("rptFilterExpCategory")}</label>
              <select className="rpt-filter-select" value={expCategoryFilter} onChange={e => setExpCategoryFilter(e.target.value)}>
                <option value="all">{t("rptFilterAllCategories")}</option>
                {expenseCategories.map(c => <option key={c.category_id} value={c.category_id}>{c.title}</option>)}
              </select>
            </div>

            <div className="rpt-filter-item">
              <label className="rpt-filter-label"><FaChartBar /> {t("rptFilterSource")}</label>
              <select className="rpt-filter-select" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
                <option value="all">{t("rptFilterAllSources")}</option>
                <option value="online">{t("rptFilterOnline")}</option>
                <option value="offline">{t("rptFilterCounter")}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── REPORT BODY ── */}
      <div className="rpt-body">
        {loading ? (
          <div className="rpt-loader-wrap"><div className="rpt-loader-om">ॐ</div><p>{t("rptLoading")}</p></div>
        ) : (
          <>
            {/* ─── DONATIONS SECTION ─── */}
            <div className="rpt-section">
              <div className="rpt-section-header">
                <div className="rpt-section-icon-wrap" style={{ background: "rgba(42,107,42,0.1)", border: "1px solid rgba(42,107,42,0.25)" }}>
                  <FaHandHoldingHeart style={{ fontSize: 18, color: "#2a6b2a" }} />
                </div>
                <div className="rpt-section-title-area">
                  <div className="rpt-section-title">{t("rptSecDonations")}</div>
                  <div className="rpt-section-meta">{filteredDonations.length} {t("rptPdfRecords")} &nbsp;·&nbsp; <strong style={{color:"#2a6b2a"}}>{fmtAmt(totalDonations)}</strong></div>
                </div>
              </div>
              {filteredDonations.length === 0 ? (
                <div className="rpt-empty-row">{t("rptNoDonations")}</div>
              ) : (
                <div className="rpt-table-scroll">
                  <table className="rpt-table rpt-desktop-only">
                    <thead><tr>
                      <th>#</th><th>{t("rptColDate")}</th><th>{t("rptColReceipt")}</th>
                      <th>{t("rptColDonorName")}</th><th>{t("rptColMobile")}</th>
                      <th>{t("rptColAddress")}</th><th>{t("rptColType")}</th>
                      <th>{t("rptColSource")}</th><th>{t("rptColRemarks")}</th><th>{t("rptColAmount")}</th>
                    </tr></thead>
                    <tbody>
                      {filteredDonations.map((d, i) => (
                        <tr key={d.donation_id}>
                          <td className="rpt-td-num">{i+1}</td>
                          <td>{fmtDate(d.created_at)}</td>
                          <td className="rpt-td-receipt">{d.receipt_number || "—"}</td>
                          <td className="rpt-td-name">{d.name}</td>
                          <td>{d.mobile_number}</td>
                          <td style={{fontSize:"12px",maxWidth:"120px"}}>{d.address || "—"}</td>
                          <td><span className="rpt-type-pill">{d.donation_type || "—"}</span></td>
                          <td><span className={`rpt-source-pill ${d.booked_by === "online" ? "online" : "counter"}`}>{d.booked_by === "online" ? t("rptSourceOnline") : t("rptSourceCounter")}</span></td>
                          <td style={{fontSize:"12px", color:"rgba(74,26,8,0.55)"}}>{d.remarks || "—"}</td>
                          <td className="rpt-td-amount">{fmtAmt(d.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="rpt-tfoot-row"><td colSpan="9" className="rpt-tfoot-label">{t("rptTotalDonations")}</td><td className="rpt-td-amount rpt-tfoot-val income">{fmtAmt(totalDonations)}</td></tr></tfoot>
                  </table>
                  <div className="rpt-mobile-only rpt-card-list">
                    {filteredDonations.map((d, i) => (
                      <div key={d.donation_id} className="rpt-mob-card">
                        <div className="rpt-mob-card-header">
                          <span className="rpt-mob-num">#{i+1}</span>
                          <span className={`rpt-source-pill ${d.booked_by === "online" ? "online" : "counter"}`}>{d.booked_by === "online" ? t("rptSourceOnline") : t("rptSourceCounter")}</span>
                        </div>
                        <div className="rpt-mob-name">{d.name}</div>
                        <div className="rpt-mob-meta">
                          <span><FaCalendarAlt className="rpt-mob-icon" />{fmtDate(d.created_at)}</span>
                          <span>{d.mobile_number}</span>
                        </div>
                        <div className="rpt-mob-meta">
                          <span className="rpt-type-pill">{d.donation_type || "—"}</span>
                          <span className="rpt-mob-amount">{fmtAmt(d.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ─── OFFERINGS SECTION ─── */}
            <div className="rpt-section">
              <div className="rpt-section-header">
                <div className="rpt-section-icon-wrap" style={{ background: "rgba(138,96,32,0.1)", border: "1px solid rgba(138,96,32,0.25)" }}>
                  <FaPray style={{ fontSize: 18, color: "#8a6020" }} />
                </div>
                <div className="rpt-section-title-area">
                  <div className="rpt-section-title">{t("rptSecOfferings")}</div>
                  <div className="rpt-section-meta">{filteredBookings.length} {t("rptPdfRecords")} &nbsp;·&nbsp; <strong style={{color:"#8a6020"}}>{fmtAmt(totalBookings)}</strong></div>
                </div>
              </div>
              {filteredBookings.length === 0 ? (
                <div className="rpt-empty-row">{t("rptNoOfferings")}</div>
              ) : (
                <div className="rpt-table-scroll">
                  <table className="rpt-table rpt-desktop-only">
                    <thead><tr>
                      <th>#</th><th>{t("rptColDate")}</th><th>{t("rptColReceipt")}</th>
                      <th>{t("rptColDevoteeName")}</th><th>{t("rptColMobile")}</th>
                      <th>{t("rptColVazhipadu")}</th><th>{t("rptColPersons")}</th>
                      <th>{t("rptColSource")}</th><th>{t("rptColAmount")}</th>
                    </tr></thead>
                    <tbody>
                      {filteredBookings.map((b, i) => (
                        <tr key={b.booking_id}>
                          <td className="rpt-td-num">{i+1}</td>
                          <td>{fmtDate(b.booking_date || b.created_at)}</td>
                          <td className="rpt-td-receipt">{b.receipt_number || "—"}</td>
                          <td className="rpt-td-name">{b.devotee_name || b.name || (Array.isArray(b.persons) && b.persons.length > 0 ? b.persons[0].name : "—")}</td>
                          <td>{b.mobile_number}</td>
                          <td><span className="rpt-type-pill">{b.category_name || b.vazhipadu_name || "—"}</span></td>
                          <td style={{textAlign:"center"}}>{b.total_persons || 1}</td>
                          <td><span className={`rpt-source-pill ${b.booked_by === "online" ? "online" : "counter"}`}>{b.booked_by === "online" ? t("rptSourceOnline") : t("rptSourceCounter")}</span></td>
                          <td className="rpt-td-amount">{fmtAmt(b.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="rpt-tfoot-row"><td colSpan="8" className="rpt-tfoot-label">{t("rptTotalOfferings")}</td><td className="rpt-td-amount rpt-tfoot-val income">{fmtAmt(totalBookings)}</td></tr></tfoot>
                  </table>
                  <div className="rpt-mobile-only rpt-card-list">
                    {filteredBookings.map((b, i) => (
                      <div key={b.booking_id} className="rpt-mob-card">
                        <div className="rpt-mob-card-header">
                          <span className="rpt-mob-num">#{i+1}</span>
                          <span className={`rpt-source-pill ${b.booked_by === "online" ? "online" : "counter"}`}>{b.booked_by === "online" ? t("rptSourceOnline") : t("rptSourceCounter")}</span>
                        </div>
                        <div className="rpt-mob-name">{b.devotee_name || b.name || (Array.isArray(b.persons) && b.persons.length > 0 ? b.persons[0].name : "—")}</div>
                        <div className="rpt-mob-meta">
                          <span><FaCalendarAlt className="rpt-mob-icon" />{fmtDate(b.booking_date || b.created_at)}</span>
                          <span><FaPray className="rpt-mob-icon" />{b.category_name || b.vazhipadu_name || "—"}</span>
                        </div>
                        <div className="rpt-mob-meta">
                          <span className="rpt-type-pill">{t("rptColPersons")}: {b.total_persons || 1}</span>
                          <span className="rpt-mob-amount">{fmtAmt(b.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ─── EXPENSES SECTION ─── */}
            <div className="rpt-section">
              <div className="rpt-section-header">
                <div className="rpt-section-icon-wrap" style={{ background: "rgba(139,26,26,0.1)", border: "1px solid rgba(139,26,26,0.25)" }}>
                  <FaMoneyBillWave style={{ fontSize: 18, color: "#8b1a1a" }} />
                </div>
                <div className="rpt-section-title-area">
                  <div className="rpt-section-title">{t("rptSecExpenses")}</div>
                  <div className="rpt-section-meta">{filteredExpenses.length} {t("rptPdfRecords")} &nbsp;·&nbsp; <strong style={{color:"#8b1a1a"}}>{fmtAmt(totalExpenses)}</strong></div>
                </div>
              </div>
              {filteredExpenses.length === 0 ? (
                <div className="rpt-empty-row">{t("rptNoExpenses")}</div>
              ) : (
                <div className="rpt-table-scroll">
                  <table className="rpt-table rpt-desktop-only">
                    <thead><tr>
                      <th>#</th><th>{t("rptColDate")}</th><th>{t("rptColCategory")}</th>
                      <th>{t("rptColExpType")}</th><th>{t("rptColTitle")}</th><th>{t("rptColAmount")}</th>
                    </tr></thead>
                    <tbody>
                      {filteredExpenses.map((e, i) => (
                        <tr key={e.expense_id}>
                          <td className="rpt-td-num">{i+1}</td>
                          <td>{fmtDate(e.expense_date)}</td>
                          <td><span className="rpt-type-pill">{e.category_title || "—"}</span></td>
                          <td><span className="rpt-type-pill" style={{borderColor:"rgba(139,26,26,0.2)",color:"#8b1a1a",background:"rgba(139,26,26,0.06)"}}>{e.category_type || "—"}</span></td>
                          <td className="rpt-td-name">{e.title}</td>
                          <td className="rpt-td-amount expense">{fmtAmt(e.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="rpt-tfoot-row"><td colSpan="5" className="rpt-tfoot-label">{t("rptTotalExpenses")}</td><td className="rpt-td-amount rpt-tfoot-val expense">{fmtAmt(totalExpenses)}</td></tr></tfoot>
                  </table>
                  <div className="rpt-mobile-only rpt-card-list">
                    {filteredExpenses.map((e, i) => (
                      <div key={e.expense_id} className="rpt-mob-card expense-card">
                        <div className="rpt-mob-card-header">
                          <span className="rpt-mob-num">#{i+1}</span>
                          <span className="rpt-type-pill">{e.category_title || "—"}</span>
                        </div>
                        <div className="rpt-mob-name">{e.title}</div>
                        <div className="rpt-mob-meta">
                          <span><FaCalendarAlt className="rpt-mob-icon" />{fmtDate(e.expense_date)}</span>
                          <span className="rpt-mob-amount expense">{fmtAmt(e.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ─── FINANCIAL SUMMARY SECTION ─── */}
            <div className="rpt-section rpt-summary-section">
              <div className="rpt-section-header rpt-summary-header">
                <div className="rpt-section-icon-wrap" style={{ background: netBalance >= 0 ? "rgba(26,90,42,0.15)" : "rgba(139,26,26,0.15)", border: `1px solid ${netBalance >= 0 ? "rgba(26,90,42,0.3)" : "rgba(139,26,26,0.3)"}` }}>
                  <FaBalanceScale style={{ fontSize: 18, color: netBalance >= 0 ? "#1a5a2a" : "#8b1a1a" }} />
                </div>
                <div className="rpt-section-title-area">
                  <div className="rpt-section-title" style={{color: "var(--gold-bright)"}}>{t("rptSecSummary")}</div>
                  <div className="rpt-section-meta" style={{color:"rgba(232,196,106,0.5)"}}>{periodLabel}</div>
                </div>
              </div>
              <div className="rpt-summary-grid">
                <div className="rpt-sumrow">
                  <div className="rpt-sumrow-label"><FaHandHoldingHeart /> {t("rptSummaryDonIncome")}</div>
                  <div className="rpt-sumrow-val" style={{color:"#2a6b2a"}}>{fmtAmt(totalDonations)}</div>
                </div>
                <div className="rpt-sumrow">
                  <div className="rpt-sumrow-label"><FaPray /> {t("rptSummaryOfferIncome")}</div>
                  <div className="rpt-sumrow-val" style={{color:"#2a6b2a"}}>{fmtAmt(totalBookings)}</div>
                </div>
                <div className="rpt-sumrow rpt-sumrow-gross">
                  <div className="rpt-sumrow-label"><FaArrowUp /> {t("rptSummaryGrossIncome")}</div>
                  <div className="rpt-sumrow-val" style={{color:"#1a4a1a", fontSize:"18px"}}>{fmtAmt(totalIncome)}</div>
                </div>
                <div className="rpt-sumrow">
                  <div className="rpt-sumrow-label"><FaArrowDown /> {t("rptTotalExpenses")}</div>
                  <div className="rpt-sumrow-val" style={{color:"#8b1a1a"}}>{fmtAmt(totalExpenses)}</div>
                </div>
                <div className={`rpt-sumrow rpt-sumrow-balance ${netBalance >= 0 ? "pos" : "neg"}`}>
                  <div className="rpt-sumrow-label"><FaBalanceScale /> {t("rptSummaryNetBalance")}</div>
                  <div className="rpt-sumrow-val" style={{color: netBalance >= 0 ? "#1a4a1a" : "#8b1a1a", fontSize:"22px"}}>{fmtAmt(netBalance)}</div>
                </div>
              </div>
              <div className="rpt-summary-footer">
                <button className="rpt-action-btn rpt-btn-pdf rpt-btn-large" onClick={() => setPdfModal(true)}>
                  <FaFilePdf /> {t("rptBtnPreviewDownload")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── MODALS ── */}
      {pdfModal && (
        <PdfPreviewModal
          reportData={{ donations: filteredDonations, bookings: filteredBookings, expenses: filteredExpenses }}
          filters={{
            month:        filterMode === "month"        ? selectedMonth : null,
            year:         filterMode === "year"         ? selectedYear  : null,
            specificDate: filterMode === "specificDate" ? specificDate  : null,
            dateRange:    filterMode === "dateRange"    ? dateRange     : null,
          }}
          onClose={() => setPdfModal(false)}
          t={t}
          i18n={i18n}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════ */
function AreportsStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Noto+Sans+Malayalam:wght@400;700&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --text-dark: #1a0404; --text-mid: #4a1a08;
        --green: #2a6b2a; --red: #8b1a1a;
      }

      /* ══ HERO ══ */
      .rpt-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); padding: 40px 32px 0; display: flex; flex-direction: column; align-items: center; border-radius: 16px 16px 0 0; }
      .rpt-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .rpt-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .rpt-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: rptRingPulse 4s ease-in-out infinite alternate; }
      .rpt-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes rptRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }
      .rpt-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,248,231,0.06); }
      .rpt-hero-med-ring { position: absolute; inset: -6px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: rptMedSpin 12s linear infinite; }
      .rpt-hero-med-ring-2 { inset: -12px; animation-direction: reverse; animation-duration: 18s; }
      @keyframes rptMedSpin { to { transform: rotate(360deg); } }
      .rpt-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .rpt-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .rpt-hero-om { font-family: 'Cinzel Decorative', cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .rpt-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .rpt-hero-med-halo { position: absolute; inset: -20px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.06) 0%, transparent 70%); pointer-events: none; }
      .rpt-hero-text { text-align: center; z-index: 1; }
      .rpt-hero-eyebrow { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .rpt-hero-title { font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .rpt-hero-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 14px; }
      .rpt-period-chip { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 1px; color: rgba(232,196,106,0.7); background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); border-radius: 20px; padding: 5px 14px; margin-bottom: 18px; }
      .rpt-hero-rule { width: 100%; height: 1px; background: linear-gradient(90deg, transparent, rgba(232,196,106,0.3), transparent); }

      /* ══ STAT ROW ══ */
      .rpt-stat-row { display: flex; gap: 12px; width: 100%; padding: 18px 0 22px; overflow-x: auto; }
      .rpt-stat-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(201,151,58,0.15); border-top: 2px solid var(--sc, #c9973a); padding: 14px 16px; min-width: 130px; flex: 1; display: flex; flex-direction: column; align-items: center; gap: 5px; clip-path: polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%); }
      .rpt-stat-icon { font-size: 15px; color: var(--sc, var(--gold-mid)); opacity: 0.85; }
      .rpt-stat-val { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; color: var(--sc, var(--gold-mid)); text-align: center; line-height: 1.3; }
      .rpt-stat-label { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(245,228,176,0.35); }

      /* ══ TOOLBAR ══ */
      .rpt-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; padding: 20px 28px 12px; background: var(--parchment); border-bottom: 1px solid rgba(201,151,58,0.15); }
      .rpt-filter-trigger-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; padding: 10px 18px; background: rgba(201,151,58,0.08); border: 1px solid rgba(201,151,58,0.3); color: var(--gold-dim); cursor: pointer; clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%); transition: all 0.2s; }
      .rpt-filter-trigger-btn:hover { background: rgba(201,151,58,0.15); }
      .rpt-filter-badge { background: var(--gold-mid); color: white; border-radius: 50%; width: 18px; height: 18px; display: inline-flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; }
      .rpt-dd-chevron { transition: transform 0.2s; font-size: 10px; }
      .rpt-dd-chevron.open { transform: rotate(180deg); }
      .rpt-toolbar-btns { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
      .rpt-action-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 10px 18px; border: 1px solid; cursor: pointer; clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%); transition: all 0.2s; background: none; }
      .rpt-btn-large { font-size: 11px; padding: 13px 28px; clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%); }
      .rpt-btn-pdf { border-color: rgba(139,26,26,0.4); color: #8b1a1a; background: rgba(139,26,26,0.05); }
      .rpt-btn-pdf:hover { background: rgba(139,26,26,0.1); }
      .rpt-btn-refresh { border-color: rgba(201,151,58,0.35); color: var(--gold-dim); }
      .rpt-btn-refresh:hover { background: rgba(201,151,58,0.1); }
      .rpt-btn-clear { border-color: rgba(139,26,26,0.25); color: #8b1a1a; }
      .rpt-btn-clear:hover { background: rgba(139,26,26,0.07); }
      .spin { animation: rptSpin 1s linear infinite; }
      @keyframes rptSpin { to { transform: rotate(360deg); } }

      /* ══ FILTER PANEL ══ */
      .rpt-filter-panel { background: rgba(232,196,106,0.05); border-bottom: 1px solid rgba(201,151,58,0.15); padding: 16px 28px; animation: rptSlideDown 0.3s ease; }
      @keyframes rptSlideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
      .rpt-filter-row { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-start; margin-bottom: 14px; }
      .rpt-filter-row:last-child { margin-bottom: 0; }
      .rpt-filter-item { display: flex; flex-direction: column; gap: 6px; min-width: 180px; }
      .rpt-filter-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-dim); display: flex; align-items: center; gap: 5px; }
      .rpt-filter-input, .rpt-filter-select { background: rgba(253,243,227,0.8); border: 1px solid rgba(201,151,58,0.25); color: var(--text-dark); font-family: 'EB Garamond', serif; font-size: 14px; padding: 8px 12px; outline: none; clip-path: polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%); transition: border-color 0.2s; }
      .rpt-filter-input:focus, .rpt-filter-select:focus { border-color: var(--gold-mid); }
      .rpt-mode-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
      .rpt-mode-tab { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; padding: 7px 14px; border: 1px solid rgba(201,151,58,0.25); background: none; color: var(--gold-dim); cursor: pointer; clip-path: polygon(5px 0%, 100% 0%, calc(100% - 5px) 100%, 0% 100%); transition: all 0.18s; }
      .rpt-mode-tab.active { background: linear-gradient(135deg, #8b1a1a, #5a0e0e); border-color: rgba(139,26,26,0.5); color: #f5ddb0; }
      .rpt-mode-tab:hover:not(.active) { background: rgba(201,151,58,0.1); }
      .rpt-range-inputs { display: flex; align-items: center; gap: 8px; }
      .rpt-range-sep { font-family: 'Cinzel', serif; font-size: 12px; color: var(--gold-dim); }

      /* ══ BODY ══ */
      .rpt-body { background: var(--parchment); padding: 24px 28px 60px; display: flex; flex-direction: column; gap: 24px; }
      .rpt-loader-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px; gap: 12px; }
      .rpt-loader-om { font-family: 'Cinzel Decorative', cursive; font-size: 42px; color: var(--gold-mid); animation: rptOmPulse 2s ease-in-out infinite; }
      @keyframes rptOmPulse { 0%,100%{opacity:0.4;transform:scale(0.95)} 50%{opacity:1;transform:scale(1.05)} }

      /* ══ SECTION ══ */
      .rpt-section { background: white; border: 1px solid rgba(201,151,58,0.2); border-radius: 12px; overflow: hidden; box-shadow: 0 2px 16px rgba(26,4,4,0.06); }
      .rpt-section-header { display: flex; align-items: center; gap: 14px; padding: 18px 22px; background: linear-gradient(135deg, #fffbf4, #fdf3e3); border-bottom: 1px solid rgba(201,151,58,0.15); }
      .rpt-section-icon-wrap { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
      .rpt-section-title { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: var(--text-dark); letter-spacing: 0.5px; }
      .rpt-section-meta { font-family: 'EB Garamond', serif; font-size: 13px; color: var(--gold-dim); margin-top: 2px; }
      .rpt-empty-row { text-align: center; padding: 32px; font-family: 'EB Garamond', serif; font-style: italic; color: rgba(74,26,8,0.4); font-size: 15px; }

      /* ══ TABLE ══ */
      .rpt-table-scroll { overflow-x: auto; }
      .rpt-table { width: 100%; border-collapse: collapse; font-family: 'EB Garamond', serif; font-size: 14px; }
      .rpt-table thead tr { background: linear-gradient(135deg, #f5e8cc, #fdf3e3); }
      .rpt-table thead th { font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 1.2px; text-transform: uppercase; color: #6b3010; padding: 10px 12px; text-align: left; border-bottom: 1px solid rgba(201,151,58,0.25); white-space: nowrap; }
      .rpt-table tbody tr { border-bottom: 1px solid rgba(201,151,58,0.1); transition: background 0.15s; }
      .rpt-table tbody tr:hover { background: rgba(253,243,227,0.6); }
      .rpt-table tbody td { padding: 9px 12px; color: var(--text-dark); vertical-align: middle; }
      .rpt-td-num { text-align: center; font-family: 'Cinzel', serif; font-size: 10px; color: var(--gold-dim); width: 36px; }
      .rpt-td-receipt { font-size: 12px; color: var(--gold-dim); font-family: 'Cinzel', serif; }
      .rpt-td-name { font-weight: 600; }
      .rpt-td-amount { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; text-align: right; color: var(--green); white-space: nowrap; }
      .rpt-td-amount.expense { color: var(--red); }
      .rpt-tfoot-row td { background: linear-gradient(135deg, #f5e8cc, #fdf0dc); font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 10px 12px; border-top: 2px solid rgba(201,151,58,0.3); }
      .rpt-tfoot-label { text-align: right; color: #6b3010; }
      .rpt-tfoot-val { text-align: right; font-size: 14px; }
      .rpt-tfoot-val.income { color: var(--green); }
      .rpt-tfoot-val.expense { color: var(--red); }

      /* Pills */
      .rpt-type-pill { display: inline-block; font-family: 'Cinzel', serif; font-size: 8px; font-weight: 600; letter-spacing: 0.8px; text-transform: uppercase; padding: 3px 8px; background: rgba(201,151,58,0.1); border: 1px solid rgba(201,151,58,0.25); border-radius: 20px; color: var(--gold-dim); white-space: nowrap; }
      .rpt-source-pill { display: inline-block; font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 3px 8px; border-radius: 20px; white-space: nowrap; }
      .rpt-source-pill.online { background: rgba(42,107,42,0.1); border: 1px solid rgba(42,107,42,0.25); color: #2a6b2a; }
      .rpt-source-pill.counter { background: rgba(139,26,26,0.08); border: 1px solid rgba(139,26,26,0.2); color: #8b1a1a; }

      /* ══ SUMMARY SECTION ══ */
      .rpt-summary-header { background: linear-gradient(135deg, #1a0404, #2c0808) !important; border-bottom: 1px solid rgba(201,151,58,0.2) !important; }
      .rpt-summary-grid { padding: 8px 22px; }
      .rpt-sumrow { display: flex; align-items: center; justify-content: space-between; padding: 13px 16px; border-bottom: 1px solid rgba(201,151,58,0.1); }
      .rpt-sumrow:last-child { border-bottom: none; }
      .rpt-sumrow-gross { background: rgba(42,107,42,0.05); }
      .rpt-sumrow-balance.pos { background: rgba(26,90,42,0.07); }
      .rpt-sumrow-balance.neg { background: rgba(139,26,26,0.05); }
      .rpt-sumrow-label { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; letter-spacing: 0.8px; color: var(--text-mid); display: flex; align-items: center; gap: 8px; }
      .rpt-sumrow-val { font-family: 'Cinzel', serif; font-size: 15px; font-weight: 700; }
      .rpt-summary-footer { display: flex; justify-content: center; padding: 20px 22px 24px; }

      /* ══ MOBILE CARDS ══ */
      .rpt-mobile-only { display: none !important; }
      .rpt-desktop-only { display: table; }
      .rpt-card-list { display: flex; flex-direction: column; gap: 10px; padding: 14px; }
      .rpt-mob-card { background: linear-gradient(145deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.2); border-radius: 10px; padding: 12px 14px; box-shadow: 0 2px 8px rgba(26,4,4,0.06); }
      .rpt-mob-card.expense-card { border-color: rgba(139,26,26,0.15); }
      .rpt-mob-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
      .rpt-mob-num { font-family: 'Cinzel', serif; font-size: 10px; color: var(--gold-dim); font-weight: 700; }
      .rpt-mob-name { font-family: 'EB Garamond', serif; font-size: 15px; font-weight: 600; color: var(--text-dark); margin-bottom: 6px; }
      .rpt-mob-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-family: 'EB Garamond', serif; font-size: 13px; color: rgba(74,26,8,0.6); margin-bottom: 4px; }
      .rpt-mob-icon { font-size: 10px; margin-right: 3px; color: var(--gold-dim); }
      .rpt-mob-amount { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: var(--green); }
      .rpt-mob-amount.expense { color: var(--red); }

      /* ══ PDF MODAL ══ */
      .rpt-modal-overlay { position: fixed; inset: 0; background: rgba(6,1,1,0.9); display: flex; align-items: flex-start; justify-content: center; z-index: 9999; padding: 20px; overflow-y: auto; backdrop-filter: blur(6px); }
      .rpt-pdf-modal { background: #2a2218; border: 1px solid rgba(201,151,58,0.25); border-radius: 10px; width: 100%; max-width: 1020px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 40px 120px rgba(0,0,0,0.85); margin: auto; }
      /* Modal header chrome */
      .rpt-pdf-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 22px; background: linear-gradient(135deg, #1a0c04, #2e0c0c); border-bottom: 2px solid rgba(201,151,58,0.35); flex-wrap: wrap; gap: 10px; }
      .rpt-pdf-modal-title { display: flex; align-items: center; gap: 12px; }
      .rpt-pdf-hicon-wrap { width: 38px; height: 38px; border-radius: 50%; background: rgba(232,196,106,0.1); border: 1px solid rgba(232,196,106,0.35); display: flex; align-items: center; justify-content: center; color: var(--gold-bright); font-size: 17px; flex-shrink: 0; }
      .rpt-pdf-modal-eyebrow { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 2px; text-transform: uppercase; color: rgba(232,196,106,0.38); }
      .rpt-pdf-modal-name { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: var(--gold-bright); letter-spacing: 0.5px; }
      .rpt-pdf-modal-actions { display: flex; align-items: center; gap: 10px; }
      .rpt-pdf-action-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 9px 20px; border: 1px solid; cursor: pointer; background: none; transition: all 0.2s; clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%); }
      .rpt-pdf-print { border-color: rgba(42,140,42,0.5); color: #7acc7a; }
      .rpt-pdf-print:hover { background: rgba(42,107,42,0.18); color: #a0eea0; }
      .rpt-pdf-download { border-color: rgba(201,151,58,0.5); color: #e8c46a; }
      .rpt-pdf-download:hover { background: rgba(201,151,58,0.15); color: #f5e090; }
      .rpt-pdf-download:disabled { opacity: 0.55; cursor: not-allowed; }
      .rpt-pdf-close-btn { width: 34px; height: 34px; border-radius: 50% !important; clip-path: none !important; border-color: rgba(200,100,100,0.3) !important; color: rgba(220,130,130,0.7); padding: 0 !important; display: flex; align-items: center; justify-content: center; }
      .rpt-pdf-close-btn:hover { background: rgba(139,26,26,0.25); color: #fde8c8; }
      /* PDF modal lang button */
      .rpt-pdf-lang-btn { display: inline-flex; align-items: center; gap: 6px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 7px 13px 7px 10px; background: rgba(232,196,106,0.07); border: 1px solid rgba(232,196,106,0.25); border-radius: 20px; color: rgba(232,196,106,0.75); cursor: pointer; transition: all 0.22s ease; }
      .rpt-pdf-lang-btn:hover { background: rgba(232,196,106,0.14); border-color: rgba(232,196,106,0.5); color: #e8c46a; }
      .rpt-pdf-lang-icon { font-size: 11px; color: var(--gold-mid, #c9973a); flex-shrink: 0; }
      .rpt-pdf-lang-label { font-family: 'Noto Sans Malayalam', 'EB Garamond', serif; font-size: 11px; letter-spacing: 0; color: rgba(232,196,106,0.9); }
      .rpt-pdf-lang-badge { font-size: 7.5px; letter-spacing: 1px; color: rgba(232,196,106,0.4); border-left: 1px solid rgba(232,196,106,0.2); padding-left: 6px; }
      /* Hero lang button */
      .rpt-lang-btn { position: absolute; top: 18px; right: 20px; display: inline-flex; align-items: center; gap: 6px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 7px 13px 7px 10px; background: rgba(232,196,106,0.07); border: 1px solid rgba(232,196,106,0.25); border-radius: 20px; color: rgba(232,196,106,0.75); cursor: pointer; z-index: 10; transition: all 0.22s ease; backdrop-filter: blur(4px); }
      .rpt-lang-btn:hover { background: rgba(232,196,106,0.14); border-color: rgba(232,196,106,0.5); color: #e8c46a; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(201,151,58,0.18); }
      .rpt-lang-icon { font-size: 11px; color: #c9973a; flex-shrink: 0; }
      .rpt-lang-label { font-family: 'Noto Sans Malayalam', 'EB Garamond', serif; font-size: 11px; letter-spacing: 0; color: rgba(232,196,106,0.9); }
      .rpt-lang-current { font-size: 7.5px; letter-spacing: 1px; color: rgba(232,196,106,0.4); border-left: 1px solid rgba(232,196,106,0.2); padding-left: 6px; }
      /* Spin animation for download */
      .rpt-pdf-spin { display: inline-block; animation: rptSpinAnim 1s linear infinite; font-size: 12px; }
      @keyframes rptSpinAnim { to { transform: rotate(360deg); } }
      /* Scroll container — simulate print preview background */
      .rpt-pdf-scroll { overflow-y: auto; max-height: calc(95vh - 70px); background: #3a3228; padding: 32px 28px; }
      .rpt-pdf-canvas { max-width: 860px; margin: 0 auto; }
      /* The white paper — drop shadow simulates real page */
      .rpt-pdf-paper { background: #ffffff; box-shadow: 0 8px 40px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(0,0,0,0.04); border-radius: 1px; overflow: hidden; font-family: 'EB Garamond', serif; }
      /* In-modal document styles that mirror print CSS */
      .rpt-pdf-paper .pdf-header { background: linear-gradient(135deg,#2e0c0c 0%,#5a1515 60%,#3d0c0c 100%); padding: 32px 44px 26px; position: relative; overflow: hidden; }
      .rpt-pdf-paper .pdf-header::before { content: 'ॐ'; position: absolute; right: 32px; top: 50%; transform: translateY(-50%); font-size: 110px; color: rgba(232,196,106,0.06); font-family: 'Cinzel Decorative', cursive; line-height: 1; pointer-events: none; }
      .rpt-pdf-paper .pdf-header-gold-bar { position: absolute; bottom: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg,transparent 0%,#8a6020 20%,#e8c46a 50%,#8a6020 80%,transparent 100%); }
      .rpt-pdf-paper .pdf-hdr-row1 { display: flex; align-items: center; gap: 18px; margin-bottom: 16px; }
      .rpt-pdf-paper .pdf-hdr-seal { width: 68px; height: 68px; border-radius: 50%; background: rgba(232,196,106,0.1); border: 2px solid rgba(232,196,106,0.4); display: flex; align-items: center; justify-content: center; font-size: 26px; color: #e8c46a; font-family: 'Cinzel Decorative', cursive; flex-shrink: 0; box-shadow: 0 0 24px rgba(232,196,106,0.1); overflow: hidden; position: relative; }
      .rpt-pdf-paper .pdf-hdr-logo-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
      .rpt-pdf-paper .pdf-hdr-logo-fallback { display: none; font-size: 26px; color: #e8c46a; font-family: 'Cinzel Decorative', cursive; align-items: center; justify-content: center; }
      .rpt-pdf-paper .pdf-hdr-temple-mal { font-family: 'Noto Sans Malayalam', 'Manjari', serif; font-size: 17px; font-weight: 700; color: #fff8e7; line-height: 1.4; margin-bottom: 3px; }
      .rpt-pdf-paper .pdf-hdr-temple-name { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: #e8c46a; letter-spacing: 1.5px; line-height: 1.3; }
      .rpt-pdf-paper .pdf-hdr-temple-sub { font-family: 'EB Garamond', serif; font-size: 12.5px; color: rgba(232,196,106,0.5); letter-spacing: 1px; margin-top: 5px; }
      .rpt-pdf-paper .pdf-hdr-divider { height: 1px; background: linear-gradient(90deg,transparent,rgba(232,196,106,0.3),transparent); margin: 0 0 14px; }
      .rpt-pdf-paper .pdf-hdr-row2 { display: flex; align-items: flex-end; justify-content: space-between; }
      .rpt-pdf-paper .pdf-hdr-report-label { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(232,196,106,0.55); margin-bottom: 4px; }
      .rpt-pdf-paper .pdf-hdr-report-title { font-family: 'Cinzel', serif; font-size: 19px; font-weight: 700; color: #e8c46a; letter-spacing: 1.5px; }
      .rpt-pdf-paper .pdf-hdr-period { font-family: 'EB Garamond', serif; font-size: 14px; color: rgba(232,196,106,0.75); margin-top: 5px; font-style: italic; }
      .rpt-pdf-paper .pdf-hdr-meta { text-align: right; }
      .rpt-pdf-paper .pdf-hdr-meta-line { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(232,196,106,0.4); letter-spacing: 1px; line-height: 1.9; }
      .rpt-pdf-paper .pdf-body { padding: 28px 44px 44px; }
      /* Summary strip */
      .rpt-pdf-paper .pdf-summary-strip { display: grid; grid-template-columns: repeat(5,1fr); border: 1.5px solid #e0c070; border-radius: 5px; overflow: hidden; margin-bottom: 30px; box-shadow: 0 2px 8px rgba(201,151,58,0.08); }
      .rpt-pdf-paper .pdf-sc { padding: 14px 10px; text-align: center; border-right: 1px solid #e8d89a; }
      .rpt-pdf-paper .pdf-sc:last-child { border-right: none; }
      .rpt-pdf-paper .pdf-sc.income { background: linear-gradient(180deg,#f2fff2,#fff); }
      .rpt-pdf-paper .pdf-sc.expense { background: linear-gradient(180deg,#fff5f5,#fff); }
      .rpt-pdf-paper .pdf-sc.balance-pos { background: linear-gradient(180deg,#e8f5e8,#f0fff0); }
      .rpt-pdf-paper .pdf-sc.balance-neg { background: linear-gradient(180deg,#fce8e8,#fff5f5); }
      .rpt-pdf-paper .pdf-sc-label { font-family: 'Cinzel', serif; font-size: 7px; letter-spacing: 1.5px; text-transform: uppercase; color: #8a6020; margin-bottom: 7px; }
      .rpt-pdf-paper .pdf-sc-val { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; letter-spacing: 0.5px; }
      .rpt-pdf-paper .pdf-sc-val.green { color: #1a5c1a; }
      .rpt-pdf-paper .pdf-sc-val.red { color: #8b1a1a; }
      .rpt-pdf-paper .pdf-sc-note { font-family: 'EB Garamond', serif; font-size: 11px; color: #b8a070; margin-top: 4px; }
      /* Sections */
      .rpt-pdf-paper .pdf-section { margin-bottom: 30px; }
      .rpt-pdf-paper .pdf-section-header { display: flex; align-items: stretch; gap: 0; margin-bottom: 12px; }
      .rpt-pdf-paper .pdf-sec-accent { width: 5px; background: linear-gradient(180deg,#8b1a1a,#c9973a); border-radius: 3px; flex-shrink: 0; margin-right: 12px; }
      .rpt-pdf-paper .pdf-sec-accent.green { background: linear-gradient(180deg,#2a6b2a,#5a9a5a); }
      .rpt-pdf-paper .pdf-sec-accent.gold { background: linear-gradient(180deg,#8a6020,#c9973a); }
      .rpt-pdf-paper .pdf-sec-title-block { flex: 1; border-bottom: 1.5px solid #e0c070; padding-bottom: 7px; }
      .rpt-pdf-paper .pdf-sec-title { font-family: 'Cinzel', serif; font-size: 11.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #3d0c0c; }
      .rpt-pdf-paper .pdf-sec-subtitle { font-family: 'EB Garamond', serif; font-size: 12px; color: #b8a070; margin-top: 3px; }
      /* Tables */
      .rpt-pdf-paper table { width: 100%; border-collapse: collapse; font-size: 12.5px; font-family: 'EB Garamond', serif; }
      .rpt-pdf-paper thead tr { background: linear-gradient(135deg,#2e0c0c,#4a1010); }
      .rpt-pdf-paper thead th { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 1.5px; text-transform: uppercase; color: #e8c46a; padding: 10px 11px; text-align: left; font-weight: 600; border-bottom: 2px solid #c9973a; }
      .rpt-pdf-paper tbody tr { border-bottom: 1px solid #f0e8d0; }
      .rpt-pdf-paper tbody tr:nth-child(even) { background: #fffbf3; }
      .rpt-pdf-paper tbody td { padding: 9px 11px; color: #2c1008; vertical-align: top; line-height: 1.45; }
      .rpt-pdf-paper tfoot tr { border-top: 2px solid #c9973a; }
      .rpt-pdf-paper tfoot td { background: linear-gradient(135deg,#3d0c0c,#2e0c0c); font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; padding: 10px 11px; color: #e8c46a; letter-spacing: 1px; }
      .rpt-pdf-paper .tfoot-amt { color: #f5e090; text-align: right; }
      .rpt-pdf-paper .td-num { text-align: center; color: #c9973a; font-family: 'Cinzel', serif; font-size: 10px; width: 26px; font-weight: 600; }
      .rpt-pdf-paper .td-amount { font-family: 'Cinzel', serif; font-weight: 700; text-align: right; color: #1a5c1a; white-space: nowrap; }
      .rpt-pdf-paper .td-amount.red { color: #8b1a1a; }
      .rpt-pdf-paper .td-receipt { font-size: 11px; color: #7a5020; font-family: 'Cinzel', serif; }
      .rpt-pdf-paper .td-pill { display: inline-block; font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; padding: 3px 9px; border: 1px solid rgba(201,151,58,0.35); border-radius: 20px; color: #7a5020; background: rgba(201,151,58,0.1); white-space: nowrap; }
      .rpt-pdf-paper .td-bold { font-weight: 600; color: #1a0404; }
      .rpt-pdf-paper .td-center { text-align: center; }
      /* Breakdown mini table */
      .rpt-pdf-paper .pdf-breakdown { margin-top: 10px; margin-left: auto; width: 46%; border: 1px solid #e8d89a; border-radius: 4px; overflow: hidden; }
      .rpt-pdf-paper .pdf-breakdown thead tr { background: linear-gradient(135deg,#f5e8cc,#ede0b0); }
      .rpt-pdf-paper .pdf-breakdown thead th { color: #5a3010; font-size: 7px; }
      .rpt-pdf-paper .pdf-breakdown tbody td { background: #fff !important; }
      /* Category header */
      .rpt-pdf-paper .pdf-cat-header { display: flex; align-items: center; gap: 10px; margin: 16px 0 7px; padding: 8px 14px; background: linear-gradient(90deg,rgba(201,151,58,0.12),transparent); border-left: 3px solid #c9973a; border-radius: 0 4px 4px 0; }
      .rpt-pdf-paper .pdf-cat-name { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; color: #5a3010; letter-spacing: 1px; text-transform: uppercase; }
      .rpt-pdf-paper .pdf-cat-type { font-size: 11px; color: #b8a070; font-style: italic; }
      .rpt-pdf-paper .pdf-cat-total { margin-left: auto; font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; color: #8b1a1a; }
      /* Financial summary rows */
      .rpt-pdf-paper .pdf-fin-summary { border: 1.5px solid #e0c070; border-radius: 5px; overflow: hidden; }
      .rpt-pdf-paper .pdf-fs-row { display: flex; align-items: center; justify-content: space-between; padding: 13px 20px; border-bottom: 1px solid #f0e8d0; }
      .rpt-pdf-paper .pdf-fs-row:last-child { border-bottom: none; }
      .rpt-pdf-paper .pdf-fs-label { font-family: 'EB Garamond', serif; font-size: 14px; color: #4a1a08; display: flex; align-items: center; gap: 9px; }
      .rpt-pdf-paper .pdf-fs-label .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
      .rpt-pdf-paper .pdf-fs-val { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; }
      .rpt-pdf-paper .pdf-fs-val.green { color: #1a5c1a; }
      .rpt-pdf-paper .pdf-fs-val.red { color: #8b1a1a; }
      .rpt-pdf-paper .pdf-fs-row.gross { background: rgba(42,107,42,0.06); }
      .rpt-pdf-paper .pdf-fs-row.gross .pdf-fs-label { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; color: #1a4a1a; letter-spacing: 1px; }
      .rpt-pdf-paper .pdf-fs-row.gross .pdf-fs-val { font-size: 17px; }
      .rpt-pdf-paper .pdf-fs-row.net-pos { background: linear-gradient(90deg,rgba(42,107,42,0.1),rgba(42,107,42,0.04)); border-top: 2px solid #2a6b2a; }
      .rpt-pdf-paper .pdf-fs-row.net-neg { background: linear-gradient(90deg,rgba(139,26,26,0.1),rgba(139,26,26,0.04)); border-top: 2px solid #8b1a1a; }
      .rpt-pdf-paper .pdf-fs-row.net-pos .pdf-fs-label, .rpt-pdf-paper .pdf-fs-row.net-neg .pdf-fs-label { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; letter-spacing: 1px; }
      .rpt-pdf-paper .pdf-fs-row.net-pos .pdf-fs-val, .rpt-pdf-paper .pdf-fs-row.net-neg .pdf-fs-val { font-size: 21px; }
      /* Footer */
      .rpt-pdf-paper .pdf-footer { margin-top: 36px; padding: 18px 0 0; border-top: 1px solid #e0c070; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
      .rpt-pdf-paper .pdf-footer-text { font-family: 'EB Garamond', serif; font-size: 11.5px; color: #c0a878; line-height: 1.8; font-style: italic; }
      .rpt-pdf-paper .pdf-footer-om { font-size: 30px; color: rgba(139,26,26,0.16); }
      .rpt-pdf-paper .pdf-footer-seal-text { font-family: 'Cinzel', serif; font-size: 7px; letter-spacing: 2px; color: #d4b87a; text-transform: uppercase; text-align: center; margin-top: 3px; }

      /* ══ TOAST ══ */
      .rpt-toast-container { position: fixed; top: 20px; right: 20px; z-index: 99999; }
      .rpt-toast-card { position: relative; display: flex; align-items: center; gap: 14px; background: linear-gradient(135deg, #FDF3E3, #faf0dc); border: 1px solid var(--border-color); clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%); padding: 16px 42px 16px 16px; min-width: 280px; max-width: 380px; box-shadow: 0 12px 40px rgba(26,4,4,0.2); }
      .rpt-toast-top-bar { position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--accent), transparent); }
      .rpt-corner { position: absolute; width: 6px; height: 6px; border-color: var(--border-color); border-style: solid; }
      .rpt-corner.tl { top: 3px; left: 3px; border-width: 1px 0 0 1px; }
      .rpt-corner.tr { top: 3px; right: 3px; border-width: 1px 1px 0 0; }
      .rpt-corner.bl { bottom: 3px; left: 3px; border-width: 0 0 1px 1px; }
      .rpt-corner.br { bottom: 3px; right: 3px; border-width: 0 1px 1px 0; }
      .rpt-toast-medallion { width: 36px; height: 36px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; position: relative; flex-shrink: 0; }
      .rpt-toast-spin-ring { position: absolute; inset: -4px; border-radius: 50%; border: 1px dashed currentColor; opacity: 0.3; animation: rptSpin 3s linear infinite; }
      .rpt-toast-icon-inner { font-size: 14px; }
      .rpt-toast-content { flex: 1; }
      .rpt-toast-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 3px; }
      .rpt-toast-symbol { font-size: 8px; }
      .rpt-toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; }
      .rpt-toast-rule { height: 1px; margin: 4px 0; }
      .rpt-toast-message { font-family: 'EB Garamond', serif; font-size: 13.5px; color: var(--text-mid); line-height: 1.4; }
      .rpt-toast-close { position: absolute; top: 8px; right: 10px; background: none; border: none; cursor: pointer; font-size: 12px; }
      .rpt-toast-bottom-rule { position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, var(--border-color), transparent); }

      /* ══ RESPONSIVE ══ */
      @media (max-width: 768px) {
        .rpt-desktop-only { display: none !important; }
        .rpt-mobile-only  { display: flex !important; }
        .rpt-hero { padding: 22px 16px 0; border-radius: 0; }
        .rpt-hero-title { font-size: 20px; }
        .rpt-hero-rings { display: none; }
        .rpt-hero-om { font-size: 24px; }
        .rpt-stat-row { flex-wrap: wrap; gap: 8px; padding: 14px 0 16px; }
        .rpt-stat-card { min-width: calc(50% - 8px); flex: unset; clip-path: none; border-radius: 8px; padding: 10px; }
        .rpt-stat-val { font-size: 11px; }
        .rpt-toolbar { padding: 12px 14px; gap: 8px; flex-direction: column; align-items: stretch; }
        .rpt-filter-trigger-btn, .rpt-action-btn { clip-path: none; border-radius: 28px; }
        .rpt-filter-trigger-btn { justify-content: space-between; padding: 11px 16px; }
        .rpt-toolbar-btns { width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-left: 0; }
        .rpt-action-btn { justify-content: center; padding: 11px 10px; font-size: 9px; }
        .rpt-filter-panel { padding: 12px 14px; }
        .rpt-filter-row { flex-direction: column; gap: 10px; }
        .rpt-filter-item { min-width: unset; }
        .rpt-filter-input, .rpt-filter-select { clip-path: none; border-radius: 8px; }
        .rpt-mode-tab { clip-path: none; border-radius: 20px; font-size: 8px; padding: 6px 10px; }
        .rpt-body { padding: 14px 14px 48px; }
        .rpt-section-header { padding: 14px 16px; }
        .rpt-summary-grid { padding: 8px 14px; }
        .rpt-sumrow { flex-direction: column; align-items: flex-start; gap: 4px; padding: 10px 12px; }
        .rpt-sumrow-val { font-size: 16px !important; }
        .rpt-modal-overlay { padding: 0; align-items: flex-start; }
        .rpt-pdf-modal { border-radius: 0; }
        .rpt-pdf-paper { padding: 24px 16px; }
        .rpt-pdf-modal-header { padding: 12px 14px; }
        .rpt-pdf-action-btn { clip-path: none; border-radius: 20px; padding: 8px 12px; font-size: 8.5px; }
        .rpt-pdf-lang-label { display: none; }
        .rpt-lang-btn { top: 12px; right: 12px; padding: 6px 10px 6px 8px; }
        .rpt-lang-label { display: none; }
        .rpt-btn-large { clip-path: none; border-radius: 28px; width: 100%; justify-content: center; }
      }
      @media (max-width: 420px) {
        .rpt-hero-title { font-size: 17px; }
        .rpt-stat-card { min-width: calc(50% - 6px); }
      }
    `}</style>
  );
}